<template>
  <div class="advertiser-page">
    <div class="advertiser-layout">
      <aside class="group-sidebar" v-loading="groupListLoading">
        <div class="group-sidebar__header">
          <div>
            <div class="group-sidebar__eyebrow">账户分组</div>
            <div class="group-sidebar__title">按分组快速筛选广告主</div>
            <div class="group-sidebar__desc">将常用账户沉淀到分组里，查找和批量管理都会更顺手。</div>
          </div>
          <div class="group-sidebar__actions">
            <el-button size="mini" icon="el-icon-refresh" @click="refreshGroupOptions(true)">
              刷新
            </el-button>
          </div>
        </div>

        <div class="group-sidebar__summary">
          <div class="group-sidebar__summary-item">
            <span class="group-sidebar__summary-label">自定义分组</span>
            <strong>{{ groupList.length }}</strong>
          </div>
          <div class="group-sidebar__summary-item">
            <span class="group-sidebar__summary-label">当前视图</span>
            <strong>{{ selectedGroupFilterLabel }}</strong>
          </div>
        </div>

        <el-input
          v-model.trim="groupSidebarKeyword"
          clearable
          size="small"
          prefix-icon="el-icon-search"
          placeholder="搜索分组名称"
          class="group-sidebar__search"
        />

        <div class="group-sidebar__list">
          <div
            v-for="item in groupSidebarItems"
            :key="item.key"
            :class="['group-sidebar__item', { 'is-active': isGroupFilterActive(item.key) }]"
            @click="handleSidebarGroupSelect(item.key)"
          >
            <div class="group-sidebar__item-icon">
              <i :class="item.icon"></i>
            </div>
            <div class="group-sidebar__item-content">
              <div class="group-sidebar__item-title">{{ item.label }}</div>
              <div class="group-sidebar__item-meta">
                <span class="group-sidebar__item-stat">数量 {{ item.accountCount }}</span>
                <span class="group-sidebar__item-stat">余额 {{ item.totalBalanceText }}</span>
              </div>
            </div>
            <i class="el-icon-arrow-right group-sidebar__item-arrow"></i>
          </div>

          <div v-if="groupSidebarItems.length === 2" class="group-sidebar__empty">
            <i class="el-icon-folder-opened"></i>
            <div class="group-sidebar__empty-title">还没有自定义分组</div>
            <div class="group-sidebar__empty-desc">先创建分组，再把广告主归类到不同的业务场景里。</div>
          </div>
        </div>
      </aside>

      <section class="advertiser-main">
        <div class="advertiser-main__hero">
          <div>
            <div class="advertiser-main__title">巨量千川账户管理</div>
            <div class="advertiser-main__desc">
              左侧切分组，右侧继续使用现有查询、授权和账户维护能力，页面层次会更清晰。
            </div>
          </div>
          <div class="advertiser-main__tags">
            <div class="advertiser-main__view-switch">
              <span class="advertiser-main__view-label">展示方式</span>
              <el-radio-group v-model="viewMode" size="mini">
                <el-radio-button label="list">列表</el-radio-button>
                <el-radio-button label="card">卡片</el-radio-button>
              </el-radio-group>
            </div>
            <el-tag size="small" type="primary" effect="plain">当前分组：{{ selectedGroupFilterLabel }}</el-tag>
            <el-tag size="small" effect="plain">未分组账户可直接在右侧编辑时归类</el-tag>
          </div>
        </div>

        <div class="advertiser-main__table">
          <common-table :limitHeight="true" :table-columns="tableColumns" :url="url" :operate="operate"
            :form-fields="formFields" :permission="permission" :searchs="searchs" :buttons="buttons" ref="table"
            :hide-table="isCardView" pageName="oceanEngineAdvertiser" @showAccountAuthorize="getAgentId"
            @data-loaded="handleAdvertiserTableLoaded" @table-reset="handleAdvertiserTableReset"
            @advertiser-alert-edit="openAlertDialog">
            <template #content="{ rows }">
              <div class="advertiser-card-view">
                <div v-if="rows && rows.length" class="advertiser-card-grid">
                  <article v-for="row in rows" :key="getAdvertiserRowId(row)" class="advertiser-card"
                    :class="{ 'is-readonly': row && row.readOnly }">
                    <div class="advertiser-card__header">
                      <div>
                        <div class="advertiser-card__name">{{ row.name || '--' }}</div>
                        <div class="advertiser-card__subline">
                          <span>ID {{ row.advertiserId || '--' }}</span>
                          <span>{{ getAccountTypeText(row.accountType) }}</span>
                        </div>
                      </div>
                      <el-tag size="mini" :type="row.accountStatus === 1 ? 'success' : 'danger'" effect="dark">
                        {{ row.accountStatus === 1 ? '账户正常' : '账户异常' }}
                      </el-tag>
                    </div>

                    <div class="advertiser-card__metrics">
                      <div class="advertiser-card__metric">
                        <span>余额</span>
                        <strong>{{ formatBalanceValue(row.balance) }}</strong>
                      </div>
                      <div class="advertiser-card__metric">
                        <span>分组</span>
                        <strong>{{ row.groupName || '未分组' }}</strong>
                      </div>
                      <div class="advertiser-card__metric">
                        <span>代理</span>
                        <strong>{{ getAgentName(row.agentId) }}</strong>
                      </div>
                    </div>

                    <div class="advertiser-card__alert-box" :class="{ 'is-enabled': isAlertEnabled(row) }">
                      <div class="advertiser-card__alert-top">
                        <div class="advertiser-card__alert-title">余额预警</div>
                        <el-tag size="mini" :type="getAlertTagType(row)" effect="plain">
                          {{ getAlertStatusText(row) }}
                        </el-tag>
                      </div>
                      <div class="advertiser-card__alert-desc">{{ getAlertSummaryText(row) }}</div>
                      <div v-if="getAlertContactSummary(row)" class="advertiser-card__alert-contacts">
                        联系人：{{ getAlertContactSummary(row) }}
                      </div>
                    </div>

                    <div class="advertiser-card__performance">
                      <div class="advertiser-card__performance-item">
                        <span>今日消耗</span>
                        <strong>￥{{ formatMetricAmount(row.todayConsumption) }}</strong>
                      </div>
                      <div class="advertiser-card__performance-item">
                        <span>在投计划</span>
                        <strong>{{ formatMetricCount(row.activePlanCount) }}</strong>
                      </div>
                      <div class="advertiser-card__performance-item advertiser-card__performance-item--accent">
                        <span>整体支付ROI</span>
                        <strong>{{ formatMetricRoi(row.overallPayRoi) }}</strong>
                      </div>
                    </div>

                    <div class="advertiser-card__footer">
                      <el-button size="mini" @click="refreshAdvertiserBalance(row)" :disabled="!canOperateAdvertiser(row)">
                        刷新余额
                      </el-button>
                      <el-button size="mini" type="primary" plain @click="openAlertDialog(row)"
                        :disabled="!canManageAlert(row)">
                        {{ hasAlertRule(row) ? '修改预警' : '创建预警' }}
                      </el-button>
                      <el-button v-if="hasAlertRule(row)" size="mini" type="warning" plain
                        @click="toggleAlertStatus(row)" :disabled="!canManageAlert(row)">
                        {{ isAlertEnabled(row) ? '停用预警' : '启用预警' }}
                      </el-button>
                    </div>
                  </article>
                </div>
                <div v-else class="advertiser-card-empty">
                  <i class="el-icon-files"></i>
                  <div class="advertiser-card-empty__title">当前筛选下没有可展示的账户</div>
                  <div class="advertiser-card-empty__desc">你可以调整分组或查询条件后再试。</div>
                </div>
              </div>
            </template>
          </common-table>
        </div>
      </section>
    </div>

    <el-dialog title="授权列表" :visible.sync="dialogVisible" custom-class="common-dialog" :close-on-click-modal="false"
      width="80%">
      <div class="dialog_body">
        <el-table :data="authorizationInfos" border style="width: 100%;">
          <el-table-column prop="aweme_id" label="抖音 ID"></el-table-column>
          <el-table-column prop="aweme_name" label="抖音名称"></el-table-column>
          <el-table-column prop="auth_type" label="授权类型" :formatter="formatAuthType"></el-table-column>
          <el-table-column prop="auth_status" label="授权状态" :formatter="formatAuthStatus"></el-table-column>
          <el-table-column prop="start_time" label="开始时间"></el-table-column>
          <el-table-column prop="end_time" label="结束时间"></el-table-column>
          <el-table-column label="抖音头像">
            <template #default="{ row }">
              <img :src="row.aweme_avatar" alt="avatar" style="max-width: 80px; max-height: 80px;"
                @click="handleAvatarClick(row.aweme_avatar)" />
            </template>
          </el-table-column>
        </el-table>
        <el-pagination @size-change="handleSizeChange" @current-change="handleCurrentChange" :current-page="currentPage"
          :page-sizes="[10, 20, 50, 100]" :page-size="pageSize" layout="total, sizes, prev, pager, next, jumper"
          :total="totalCount"></el-pagination>
        <!-- 头像放大层 -->
        <div v-show="isImagePopupVisible" class="image-popup" @click="handleImagePopupClose">
          <img :src="currentAvatarUrl" />
        </div>
      </div>
    </el-dialog>

    <div class="dialog">
      <el-dialog title="更新授权" :visible.sync="updateVisible" custom-class="common-dialog" :close-on-click-modal="false"
        @close="resetForm('updateForm')" width="30%">
        <div class="dialog_body">
          <el-form label-position="right" status-icon label-width="90px" :model="updateForm" ref="updateForm">
            <el-form-item label="授权码" prop="code" :rules="[{ required: true, message: '授权码不能为空', trigger: 'blur' }]">
              <el-input v-model="updateForm.code" placeholder="请输入授权码"></el-input>
            </el-form-item>

          </el-form>
        </div>
        <div slot="footer" class="dialog-footer">
          <el-button size="small" @click="updateVisible = false">取 消</el-button>
          <el-button type="primary" size="small" @click="submitBatch('updateForm')">确 定</el-button>
        </div>
      </el-dialog>
    </div>
    <el-dialog :visible.sync="groupDialogVisible" custom-class="common-dialog ocean-group-dialog"
      :close-on-click-modal="false" width="78%" @closed="handleGroupDialogClosed">
      <template #title>
        <div class="group-dialog__title-wrap">
          <div class="group-dialog__title">分组管理</div>
          <div class="group-dialog__subtitle">按代理维护分组名称，创建完成后可直接回到主表筛选和归类账户。</div>
        </div>
      </template>

      <div class="group-dialog">
        <div class="group-dialog__list-panel" v-loading="groupListLoading">
          <div class="group-dialog__panel-header">
            <div>
              <div class="group-dialog__panel-title">分组清单</div>
              <div class="group-dialog__panel-desc">点击卡片即可切换到编辑状态，删除前需要先解绑广告主。</div>
            </div>
          </div>

          <el-input
            v-model.trim="groupManageKeyword"
            clearable
            size="small"
            prefix-icon="el-icon-search"
            placeholder="搜索分组名称"
            class="group-dialog__search"
          />

          <div class="group-dialog__list">
            <div
              v-for="group in filteredGroupManagementGroups"
              :key="group.id"
              :class="['group-dialog__card', { 'is-active': isEditingGroup(group.id) }]"
              @click="prepareEditGroup(group)"
            >
              <div class="group-dialog__card-top">
                <div>
                  <div class="group-dialog__card-title">{{ group.name }}</div>
                  <div class="group-dialog__card-meta">
                    数量 {{ getGroupAccountCount(group.id) }} · 余额 {{ getGroupTotalBalanceText(group.id) }}
                  </div>
                </div>
                <el-tag size="mini" type="info" effect="plain">ID {{ group.id }}</el-tag>
              </div>
              <div class="group-dialog__card-footer">
                <span>创建时间：{{ group.createTime || '--' }}</span>
                <div class="group-dialog__card-actions">
                  <el-button v-if="canUpdateGroup" type="text" size="mini" @click.stop="prepareEditGroup(group)">
                    编辑
                  </el-button>
                  <el-button v-if="canDeleteGroup" type="text" size="mini" class="group-dialog__danger"
                    :loading="groupDeleteLoadingId === group.id" @click.stop="deleteGroup(group)">
                    删除
                  </el-button>
                </div>
              </div>
            </div>

            <div v-if="!filteredGroupManagementGroups.length" class="group-dialog__empty">
              <i class="el-icon-folder-opened"></i>
              <div class="group-dialog__empty-title">没有匹配的分组</div>
              <div class="group-dialog__empty-desc">你可以调整搜索词，或者直接在右侧创建一个新分组。</div>
            </div>
          </div>
        </div>

        <div class="group-dialog__editor-panel">
          <div class="group-dialog__editor-hero">
            <div>
              <div class="group-dialog__panel-title">{{ groupEditorTitle }}</div>
              <div class="group-dialog__panel-desc">建议用“业务线 / 店铺 / 投放目的”命名，后续查找会更直观。</div>
            </div>
            <el-tag v-if="groupEditorForm.id" size="small" effect="dark" type="primary">编辑中</el-tag>
          </div>

          <el-form ref="groupEditorForm" :model="groupEditorForm" :rules="groupFormRules" label-position="top"
            class="group-dialog__form">
            <el-form-item label="所属代理" prop="agentId">
              <el-select v-model="groupEditorForm.agentId" filterable clearable placeholder="请选择代理" style="width: 100%;">
                <el-option v-for="option in agentOptions" :key="option.id" :label="option.name" :value="option.id" />
              </el-select>
            </el-form-item>
            <el-form-item label="分组名称" prop="name">
              <el-input v-model.trim="groupEditorForm.name" maxlength="30" show-word-limit placeholder="请输入分组名称"
                @keyup.enter.native="submitGroupForm" />
            </el-form-item>
          </el-form>

          <div class="group-dialog__tips">
            <div class="group-dialog__tips-title">填写建议</div>
            <div class="group-dialog__tip-item">同一代理下分组名称不能重复，建议直接体现业务场景。</div>
            <div class="group-dialog__tip-item">如果某个分组已绑定广告主，需要先在主表里调整分组后才能删除。</div>
            <div class="group-dialog__tip-item">建好分组后，左侧导航和右侧新增/编辑表单会同步刷新。</div>
          </div>

          <div class="group-dialog__form-actions">
            <el-button @click="prepareCreateGroup">
              {{ groupEditorForm.id ? '取消编辑' : '重置表单' }}
            </el-button>
            <el-button type="primary" :loading="groupSubmitting" @click="submitGroupForm">
              {{ groupEditorForm.id ? '保存修改' : '创建分组' }}
            </el-button>
          </div>
        </div>
      </div>
    </el-dialog>
    <el-dialog title="批量修改分组" :visible.sync="batchGroupDialogVisible" custom-class="common-dialog"
      :close-on-click-modal="false" width="32%" @closed="resetBatchGroupDialog">
      <div class="dialog_body">
        <div class="batch-group-dialog__summary">
          已选择 <span>{{ batchGroupSelectionIds.length }}</span> 个账户，统一修改后会立即覆盖当前分组归属。
        </div>
        <el-form ref="batchGroupForm" :model="batchGroupForm" :rules="batchGroupRules" label-position="top">
          <el-form-item label="目标分组" prop="groupId">
            <el-select v-model="batchGroupForm.groupId" clearable placeholder="请选择目标分组" style="width: 100%;">
              <el-option v-for="option in batchGroupOptions" :key="option.id" :label="option.name" :value="option.id" />
            </el-select>
          </el-form-item>
        </el-form>
        <div class="batch-group-dialog__tips">
          <div class="batch-group-dialog__tip">选择“清空分组”后，账户会回到未分组状态。</div>
          <div class="batch-group-dialog__tip">批量修改时会校验代理与分组是否匹配，不匹配的账户不会被错误归组。</div>
        </div>
      </div>
      <div slot="footer" class="dialog-footer">
        <el-button @click="batchGroupDialogVisible = false">取 消</el-button>
        <el-button type="primary" :loading="batchGroupSubmitting" @click="submitBatchGroup">确 定</el-button>
      </div>
    </el-dialog>
    <el-dialog :visible.sync="alertDialogVisible" custom-class="common-dialog advertiser-alert-dialog"
      :close-on-click-modal="false" width="34%" @closed="resetAlertDialog">
      <template #title>
        <div class="advertiser-alert-dialog__title">{{ alertForm.id ? '修改余额预警' : '创建余额预警' }}</div>
      </template>
      <div class="dialog_body">
        <guide-panel
          dense
          class="mb20"
          title="单账户余额预警说明"
          summary="这里的预警只作用于当前广告账户。命中阈值后系统会按联系人和频率发送微信通知，不会自动充值。"
          icon="el-icon-warning-outline"
          :points="[
            '建议至少选择 1 个联系人，避免提醒漏接。',
            '如果开启频率限制，同一条规则会按所选间隔去重通知。',
            '停用后规则会保留，后续可直接再次启用。'
          ]"
        />
        <el-form ref="alertFormRef" :model="alertForm" :rules="alertFormRules" label-position="top">
          <el-form-item label="当前账户">
            <div class="advertiser-alert-dialog__account">
              <div class="advertiser-alert-dialog__account-name">{{ alertForm.accountName || '--' }}</div>
              <div class="advertiser-alert-dialog__account-id">广告主ID：{{ alertForm.advertiserId || '--' }}</div>
            </div>
          </el-form-item>
          <el-form-item label="规则名称" prop="ruleName">
            <el-input v-model.trim="alertForm.ruleName" maxlength="60" placeholder="请输入规则名称" />
          </el-form-item>
          <el-form-item label="余额阈值" prop="balanceMin">
            <el-input v-model.trim="alertForm.balanceMin" placeholder="请输入余额阈值">
              <template slot="append">元</template>
            </el-input>
          </el-form-item>
          <el-form-item label="微信联系人" prop="wxNumber">
            <el-select v-model="alertForm.wxNumber" multiple filterable clearable placeholder="请选择联系人"
              style="width: 100%;" :loading="contactLoading">
              <el-option v-for="item in contactOptions" :key="item.openId || item.id" :label="item.wechatName"
                :value="item.openId" />
            </el-select>
            <div class="advertiser-alert-dialog__helper">
              <span>没有可用联系人？</span>
              <el-button type="text" @click="goAddContrats">前往联系人页面添加</el-button>
            </div>
          </el-form-item>
          <el-form-item>
            <div class="advertiser-alert-dialog__switch-row">
              <el-switch v-model="alertForm.limitNotice" />
              <span>限制通知频率</span>
            </div>
            <el-select v-model="alertForm.interval" :disabled="!alertForm.limitNotice" placeholder="请选择通知频率"
              style="width: 100%; margin-top: 10px;">
              <el-option v-for="item in notifyIntervalOptions" :key="item.key" :label="item.value" :value="item.key" />
            </el-select>
          </el-form-item>
          <el-form-item>
            <div class="advertiser-alert-dialog__switch-row">
              <el-switch v-model="alertForm.status" :active-value="1" :inactive-value="0" />
              <span>{{ alertForm.status === 1 ? '启用规则' : '停用规则' }}</span>
            </div>
          </el-form-item>
        </el-form>
      </div>
      <div slot="footer" class="dialog-footer">
        <el-button @click="alertDialogVisible = false">取消</el-button>
        <el-button type="primary" :loading="alertDialogSubmitting" @click="submitAlertForm">保存</el-button>
      </div>
    </el-dialog>
    <div class="mask  flex-center" v-if="showAccountAuthorize">
      <div class="popup_box">
        <div class="flex-end">
          <i class="el-icon-close cp" @click="showAccountAuthorize = false"></i>
        </div>
        <div class="text-center fs20 font-bold">
          授权方式
        </div>
        <div class="pb20">
          请选择授权方式
        </div>
        <guide-panel
          dense
          class="mb20"
          title="账户授权说明"
          summary="这里不是普通资料编辑，而是给广告账户补齐投放权限。授权成功后，系统才能继续同步抖音号、素材和账户信息。"
          icon="el-icon-key"
          :points="[
            '自助授权：适合店铺管理员本人在当前设备直接完成授权。',
            '分享链接授权：适合把授权链接发给店铺老板或实际控制人远程完成，链接 15 分钟内有效。',
            '如果后续需要预览视频、图片或远程素材，还要补【敏感物料】授权；授权完成后通常需要等待 5-10 分钟再刷新查看。'
          ]"
        />
        <div class="flex-between alcenter">
          <div class="authorizeBox mr10" :class="{ 'activeAuthorizeBox': !activeAuthorizeBox }"
            @click="activeAuthorizeBox = 0">
            自助授权
            <div>
              此方式直接弹出千川授权页面完成授权，适用于抖店店铺管理员自主授权
            </div>
          </div>
          <div class="authorizeBox" :class="{ 'activeAuthorizeBox': activeAuthorizeBox }"
            @click="activeAuthorizeBox = 1">
            分享链接授权
            <div>
              点击复制链接后，可分享他人在外部浏览器打开千川授权，授权完毕后刷新页面会自动同步，适用于店铺主远程授权代运营
            </div>
          </div>
        </div>
        <div v-if="!activeAuthorizeBox">
          <div class="text-center fs20 font-bold mt20 mb20">
            【敏感物料】授权
          </div>
          <div>
            授权<span class="c_primary">【敏感物料】</span> 后可以管理远程素材，否则无法预览视频或图片信息。
          </div>
          <div>
            如需预览，请按照下图操作指引进行重新授权。
          </div>
          <div class="mb20">
            成功授权后，官方生效预计需要5-10分钟，请稍后刷新页面再进行预览查看。
          </div>
          <div class="flex-center">
            <img class="instanceDiagram" src="https://qc.uliangtech.com/dist/static/image/assetsTips.1e68a19a.png"
              alt="">
          </div>
          <div class="flex-center pt50">
            <el-button type="primary" round @click="goJumpLink">确定</el-button>
          </div>
        </div>
        <div class="c_ffb563 pt10" v-else>
          <i class="el-icon-warning"></i>
          授权链接有效期为15分钟

          <div class="flex-center pt50">
            <el-button type="primary" round @click="goCopyeLink">复制链接</el-button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import CommonTable from "@/components/CommonTable/CommonTable";
import GuidePanel from "@/components/GuidePanel/GuidePanel";

export default {
  name: 'OceanEngineAdvertiser',
  components: { CommonTable, GuidePanel },
  data() {
    const ungroupedOption = { key: 0, value: '\u672a\u5206\u7ec4' };
    const supportedAccountTypeOptions = [
      { key: 1, value: '广告账户' },
      { key: 6, value: '纵横组织下的账号' },
      { key: 7, value: '店铺账户下的账号' }
    ];
    const allAccountTypeOptions = [
      { key: 1, value: '广告账户' },
      { key: 2, value: '店铺账户' },
      { key: 3, value: '代理商账户' },
      { key: 4, value: '协助授权的纵横组织' },
      { key: 5, value: '管理员授权的纵横组织' },
      { key: 6, value: '纵横组织下的账号' },
      { key: 7, value: '店铺账户下的账号' }
    ];
    const alertStatusOptions = [
      { key: 1, value: '已启用预警' },
      { key: 0, value: '已停用预警' },
      { key: -1, value: '未设置预警' }
    ];
    const notifyIntervalOptions = [
      { key: 30, value: '每30分钟' },
      { key: 60, value: '每1小时' },
      { key: 180, value: '每3小时' },
      { key: 360, value: '每6小时' }
    ];
    return {
      showAccountAuthorize: false,
      activeAuthorizeBox: 0,
      updateVisible: false,
      viewMode: 'list',
      groupDialogVisible: false,
      groupDialogTitle: '\u5206\u7ec4\u7ba1\u7406',
      groupPermissions: [],
      groupList: [],
      groupListLoading: false,
      groupStatsLoading: false,
      groupStatsMap: {},
      groupStatsQuerySignature: '',
      groupStatsRefreshTimer: null,
      pendingGroupStatsRequest: null,
      groupSidebarKeyword: '',
      groupManageKeyword: '',
      activeGroupFilter: 'all',
      agentOptions: [],
      batchGroupDialogVisible: false,
      batchGroupSubmitting: false,
      batchGroupSelectionIds: [],
      batchGroupForm: {
        groupId: ''
      },
      batchGroupRules: {
        groupId: [{ required: true, message: '请选择目标分组', trigger: 'change' }]
      },
      groupSubmitting: false,
      groupDeleteLoadingId: null,
      groupEditorForm: {
        id: '',
        agentId: '',
        name: ''
      },
      groupFormRules: {
        agentId: [{ required: true, message: '请选择代理', trigger: 'change' }],
        name: [
          { required: true, message: '分组名称不能为空', trigger: 'blur' }
        ]
      },
      updateForm: {
        code: '',
        id: ''
      },
      authorization_infos: [],
      dialogVisible: false,
      authorizationInfos: [],
      currentPage: 1,
      pageSize: 10,
      totalCount: 0,
      isImagePopupVisible: false, // 控制头像放大层是否显示
      currentAvatarUrl: '',// 当前点击要放大的头像URL
      advertiserTableRows: [],
      balanceAlertRuleList: [],
      balanceAlertRuleMap: {},
      alertRuleLoading: false,
      contactOptions: [],
      contactLoading: false,
      alertDialogVisible: false,
      alertDialogSubmitting: false,
      currentAlertRow: null,
      alertStatusOptions,
      notifyIntervalOptions,
      alertForm: {
        id: '',
        advertiserId: '',
        agentId: '',
        accountName: '',
        ruleName: '',
        balanceMin: '',
        wxNumber: [],
        limitNotice: true,
        interval: 30,
        status: 1,
      },
      selectedId: null,
      supportedAccountTypeOptions,
      allAccountTypeOptions,
      ungroupedOption,
      searchs: [{
        name: 'agentId',
        placeholder: '代理',
        type: 'select',
        selectUrl: '/agent/getAll',
        selectData: [],
      }, {
        name: 'advertiserId',
        placeholder: '广告主ID',
        type: 'input',
      }, {
        name: 'name',
        placeholder: '账户名称',
        type: 'input'
      }, {
        name: 'groupId',
        placeholder: '账户分组',
        type: 'select',
        selectValue: [ungroupedOption]
      }, {
        name: 'accountStatus',
        placeholder: '账户状态',
        type: 'select',
        selectValue: [{
          key: 0,
          value: '异常',
        }, {
          key: 1,
          value: '正常',
        }]
      },
        {
          name: 'accountType',
          placeholder: '账户类型',
          type: 'select',
          selectValue: [
            { key: 1, value: '广告账户' },
            // { key: 2, value: '店铺账户' },
            // { key: 3, value: '代理商账户' },
            // { key: 4, value: '协助授权的纵横组织' },
            // { key: 5, value: '管理员授权的纵横组织' },
            { key: 6, value: '纵横组织下的账号' },
            { key: 7, value: '店铺账户下的账号' }
          ]
        },
        {
          name: 'balance',
          placeholder: '余额小于',
          type: 'input'
        }],
      permission: {
        add: 'oceanEngineAdvertiser:add',
        update: 'oceanEngineAdvertiser:update',
        delete: 'oceanEngineAdvertiser:delete',
        deleteBatch: 'oceanEngineAdvertiser:deleteBatch'
      },
      url: {
        get: '/oceanEngineAdvertiser/listByCondition',
        add: '/oceanEngineAdvertiser/add',
        update: '/oceanEngineAdvertiser/update',
        delete: '/oceanEngineAdvertiser/delete',
        delete_batch: '/oceanEngineAdvertiser/deleteBatch',
        status: '/oceanEngineAdvertiser/status',
      },
      tableColumns: [{
        field: 'id',
        label: 'Id',
        show: false,
        width: '100',
      }, {
        field: 'agentId',
        label: '代理',
        selectUrl: '/agent/getAll',
        selectData: [],
        show: false,
      }, {
        field: 'advertiserId',  // 字段名
        label: '广告主ID',  // 字段显示名称
        sort: false,  // 是否排序
        show: true,  // 是否展示
      }, {
        field: 'name',  // 字段名
        label: '账户名',  // 字段显示名称
        sort: false,  // 是否排序
        show: true,  // 是否展示
      }, {
        field: 'company',  // 字段名
        label: '公司名',  // 字段显示名称
        sort: false,  // 是否排序
        show: true,  // 是否展示
      }, {
        field: 'code',  // 字段名
        label: '授权码',  // 字段显示名称
        sort: false,  // 是否排序
        show: false,  // 是否展示
      }, {
        field: 'groupName',
        label: '账户分组',
        sort: false,
        show: true,
      }, {
        field: 'balance',  // 字段名
        label: '余额',  // 字段显示名称
        sort: false,  // 是否排序
        show: true,  // 是否展示
        sortable: true
      }, {
        field: 'alertStatus',
        label: '预警状态',
        sort: false,
        show: {
          type: 'tag',
          tags: [
            { key: 1, value: '已启用', type: 'success' },
            { key: 0, value: '已停用', type: 'warning' },
            { key: -1, value: '未设置', type: 'info' }
          ]
        }
      }, {
        field: 'alertThresholdText',
        label: '预警',
        sort: false,
        width: '120',
        show: {
          type: 'advertiserAlert'
        },
      }, {
        field: 'message',  // 字段名
        label: '错误信息',  // 字段显示名称
        sort: false,  // 是否排序
        show: true,  // 是否展示
      }, {
        field: 'accountStatus',  // 字段名
        label: '账户状态',  // 字段显示名称
        sort: false,  // 是否排序
        show: true,  // 是否展示
        status: true,
      }, {
        field: 'accountType',  // 字段名
        label: '账户类型',      // 字段显示名称
        sort: false,           // 是否排序
        show: {
          type: 'tag',
          effect: 'plain',
          tags: [
            { key: 1, value: '广告账户', type: 'success' },
            { key: 2, value: '店铺账户', type: 'info' },
            { key: 3, value: '代理商账户', type: 'info' },
            { key: 4, value: '协助授权的纵横组织', type: 'info' },
            { key: 5, value: '管理员授权的纵横组织', type: 'info' },
            { key: 6, value: '纵横组织下的账号', type: '' },
            { key: 7, value: '店铺账户下的账号', type: 'warning' }
          ]
        },
        selectData: [
          { id: 1, name: '广告账户' },
          // { id: 2, name: '店铺账户' },
          // { id: 3, name: '代理商账户' },
          // { id: 4, name: '协助授权的纵横组织' },
          // { id: 5, name: '管理员授权的纵横组织' },
          { id: 6, name: '纵横组织下的账号' },
          { id: 7, name: '店铺账户下的账号' }
        ]
      }, {
        field: 'createTime',
        label: '时间',
        show: true,
        width: '',
      }
      ],
      formFields: {
        addTitle: '添加',
        updateTitle: '修改',
        model: [
          {
            field: 'id',  // 字段名
            label: 'ID',  // 字段显示名称
            type: 'input',
            add: false,  // 是否添加
            update: true
          }, {
            field: 'agentId',
            label: '代理：',
            type: 'select',
            placeholder: '请选择代理',
            rules: [{ required: true, message: '请选择代理', trigger: 'blur' }],
            selectUrl: '/agent/getAll', // 复用代理选择接口
            selectData: [],
            add: true,
            update: true,
            filterable: true,
          },
          {
            field: 'advertiserId',  // 字段名
            label: '广告主ID',  // 字段显示名称
            type: 'input',
            placeholder: '请输入广告主ID',
            rules: [
              { required: true, message: '广告主ID不能为空', trigger: 'blur' }
            ],
            add: true,
            update: true,
          }, {
            field: 'code',  // 字段名
            label: '授权码',  // 字段显示名称
            type: 'input',
            placeholder: '请输入授权码',
            rules: [
              { required: true, message: '授权码不能为空', trigger: 'blur' }
            ],
            add: true,
            update: true,
          }, {
            field: 'groupId',
            label: '账户分组',
            type: 'select',
            placeholder: '请选择账户分组',
            selectData: [],
            add: true,
            update: true,
            clearable: true,
            filterable: true,
          }, {
            field: 'balance',  // 字段名
            label: '余额',  // 字段显示名称
            type: 'input',
            placeholder: '请输入余额',
            rules: [
              { required: true, message: '余额不能为空', trigger: 'blur' }
            ],
            add: false,
            update: false,
          },
        ]
      },
      operate: [{
        icon: 'el-icon-files',
        type: 'text',
        text: '查询余额',
        exist: (row) => {
          return this.$refs.table.havePermission('oceanEngineAdvertiser:list') && this.canOperateAdvertiser(row)
        },
        handle: (row) => {
          const advertiserRowId = this.getAdvertiserRowId(row)
          if (!advertiserRowId) {
            this.$message.error('未获取到账户标识')
            return
          }
          this.$axios.post({
            url: '/oceanEngineAdvertiser/search/balance/' + advertiserRowId,
            success: (res) => {
              if (res.code === 0) {
                this.$set(row, 'balance', res.data);
              } else {
                this.$message.error(res.msg);
              }
            }
          });
        }
      }, {
        icon: 'el-icon-files',
        type: 'text',
        text: '更新账户状态',
        exist: (row) => {
          return this.$refs.table.havePermission('oceanEngineAdvertiser:list') && this.canOperateAdvertiser(row)
        },
        handle: (row) => {
          const advertiserRowId = this.getAdvertiserRowId(row)
          if (!advertiserRowId) {
            this.$message.error('未获取到账户标识')
            return
          }
          this.$axios.get({
            url: '/oceanEngineAdvertiser/updateAdvertiserInfoStatus/' + advertiserRowId,
            success: (res) => {
              if (res.code === 0) {
                this.$refs.table.getData()
              } else {
                this.$message.error(res.msg);
              }
            }
          });
        }
      }, {
        icon: 'el-icon-files',
        type: 'text',
        text: '更新授权',
        exist: (row) => {
          return this.$refs.table.havePermission('oceanEngineAdvertiser:add') && this.canOperateAdvertiser(row)
        },
        handle: (row) => {
          this.updateForm.id = this.getAdvertiserRowId(row);
          this.updateVisible = true
          /*    this.$axios.post({
                url: '/oceanEngineAdvertiser/refreshToken/' + row.id,
                success: (res) => {
                  if (res.code === 0) {
                    this.$refs.table.getData()
                  } else {
                    this.$message.error('更新授权失败');
                  }
                }
              });*/
        }
      }, {
        icon: 'el-icon-files',
        type: 'text',
        text: '获取授权列表',
        exist: (row) => {
          return this.$refs.table.havePermission('oceanEngineAdvertiser:list') && this.canOperateAdvertiser(row)
        },
        handle: (row) => {
          this.selectedId = this.getAdvertiserRowId(row);
          this.currentPage = 1;
          this.authorizationInfos = [];
          this.fetchAuthorizationList();
          this.dialogVisible = true;
        }
      }],
      buttons: [{
        icon: 'el-icon-collection-tag',
        type: 'primary',
        text: '分组管理',
        permission: 'oceanEngineAdvertiserGroup:list',
        click: () => {
          this.openGroupDialog()
        }
      }, {
        icon: 'el-icon-edit-outline',
        type: 'warning',
        text: '批量修改分组',
        permission: 'oceanEngineAdvertiser:update',
        click: () => {
          this.openBatchGroupDialog()
        }
      }],
      groupSearchs: [{
        name: 'agentId',
        placeholder: '代理',
        type: 'select',
        selectUrl: '/agent/getAll',
        selectData: [],
      }, {
        name: 'name',
        placeholder: '分组名称',
        type: 'input',
      }],
      groupPermission: {
        add: 'oceanEngineAdvertiserGroup:add',
        update: 'oceanEngineAdvertiserGroup:update',
        delete: 'oceanEngineAdvertiserGroup:delete',
        deleteBatch: 'oceanEngineAdvertiserGroup:deleteBatch'
      },
      groupUrl: {
        get: '/oceanEngineAdvertiserGroup/listByCondition',
        add: '/oceanEngineAdvertiserGroup/add',
        update: '/oceanEngineAdvertiserGroup/update',
        delete: '/oceanEngineAdvertiserGroup/delete',
        delete_batch: '/oceanEngineAdvertiserGroup/deleteBatch',
        status: '/oceanEngineAdvertiserGroup/status',
      },
      groupTableColumns: [{
        field: 'id',
        label: 'Id',
        show: true,
        width: '100',
      }, {
        field: 'agentId',
        label: '代理',
        selectUrl: '/agent/getAll',
        selectData: [],
        show: {
          type: 'select',
        },
      }, {
        field: 'name',
        label: '分组名称',
        sort: false,
        show: true,
      }, {
        field: 'createTime',
        label: '时间',
        show: true,
        width: '',
      }],
      groupFormFields: {
        addTitle: '添加',
        updateTitle: '修改',
        model: [{
          field: 'id',
          label: 'ID',
          type: 'input',
          add: false,
          update: true
        }, {
          field: 'agentId',
          label: '代理：',
          type: 'select',
          placeholder: '请选择代理',
          rules: [{ required: true, message: '请选择代理', trigger: 'blur' }],
          selectUrl: '/agent/getAll',
          selectData: [],
          add: true,
          update: true,
          filterable: true,
        }, {
          field: 'name',
          label: '分组名称',
          type: 'input',
          placeholder: '请输入分组名称',
          rules: [
            { required: true, message: '分组名称不能为空', trigger: 'blur' }
          ],
          add: true,
          update: true,
        }]
      },
      copyLink: ''
    }
  },
  computed: {
    isCardView() {
      return this.viewMode === 'card'
    },
    alertFormRules() {
      return {
        ruleName: [{ required: true, message: '请输入规则名称', trigger: 'blur' }],
        balanceMin: [
          { required: true, message: '请输入余额阈值', trigger: 'blur' },
          {
            validator: (rule, value, callback) => {
              if (value === '' || value === null || value === undefined) {
                callback()
                return
              }
              const parsedValue = Number(value)
              if (!Number.isFinite(parsedValue) || parsedValue < 0) {
                callback(new Error('请输入大于等于 0 的余额阈值'))
                return
              }
              callback()
            },
            trigger: 'blur'
          }
        ],
        wxNumber: [{ type: 'array', required: true, message: '请选择至少一个联系人', trigger: 'change' }]
      }
    },
    canAddGroup() {
      return this.hasGroupPermission('oceanEngineAdvertiserGroup:add')
    },
    canUpdateGroup() {
      return this.hasGroupPermission('oceanEngineAdvertiserGroup:update')
    },
    canDeleteGroup() {
      return this.hasGroupPermission('oceanEngineAdvertiserGroup:delete')
    },
    sortedGroupList() {
      return [...this.groupList].sort((firstGroup, secondGroup) => {
        const firstAgentName = this.getAgentName(firstGroup.agentId)
        const secondAgentName = this.getAgentName(secondGroup.agentId)
        const agentCompare = firstAgentName.localeCompare(secondAgentName, 'zh-CN')
        if (agentCompare !== 0) {
          return agentCompare
        }
        return (firstGroup.name || '').localeCompare(secondGroup.name || '', 'zh-CN')
      })
    },
    groupSidebarItems() {
      const keyword = (this.groupSidebarKeyword || '').toLowerCase()
      const baseItems = [
        {
          key: 'all',
          label: '全部账户',
          accountCount: this.getGroupAccountCount('all'),
          totalBalanceText: this.getGroupTotalBalanceText('all'),
          icon: 'el-icon-menu'
        },
        {
          key: 0,
          label: '未分组账户',
          accountCount: this.getGroupAccountCount(0),
          totalBalanceText: this.getGroupTotalBalanceText(0),
          icon: 'el-icon-collection-tag'
        }
      ]
      const customItems = this.sortedGroupList
        .filter(group => {
          if (!keyword) {
            return true
          }
          return (group.name || '').toLowerCase().includes(keyword)
        })
        .map(group => ({
          key: group.id,
          label: group.name,
          accountCount: this.getGroupAccountCount(group.id),
          totalBalanceText: this.getGroupTotalBalanceText(group.id),
          icon: 'el-icon-folder',
        }))
      return baseItems.concat(customItems)
    },
    selectedGroupFilterLabel() {
      if (this.isGroupFilterActive('all')) {
        return '全部账户'
      }
      if (this.isGroupFilterActive(0)) {
        return '未分组账户'
      }
      const currentGroup = this.groupList.find(group => this.isGroupKeyEqual(group.id, this.activeGroupFilter))
      return currentGroup ? currentGroup.name : '全部账户'
    },
    filteredGroupManagementGroups() {
      const keyword = (this.groupManageKeyword || '').toLowerCase()
      return this.sortedGroupList.filter(group => {
        if (!keyword) {
          return true
        }
        return (group.name || '').toLowerCase().includes(keyword)
      })
    },
    batchGroupOptions() {
      return [{
        id: '__CLEAR__',
        name: '清空分组'
      }].concat(this.sortedGroupList.map(group => ({
        id: group.id,
        name: group.name
      })))
    },
    groupEditorTitle() {
      return this.groupEditorForm.id ? '编辑分组信息' : '新增分组'
    }
  },
  watch: {
    viewMode(nextMode) {
      if (nextMode === 'list') {
        this.scheduleTableLayout(40)
      }
    }
  },
  beforeDestroy() {
    this.clearGroupStatsRefreshTimer()
  },
  created() {
    this.optimizeAdvertiserTableColumns()
    this.initAccountTypeConfig()
    this.loadGroupPermissions()
    this.loadAgentOptions()
    this.initGroupConfig()
  },
  methods: {
    optimizeAdvertiserTableColumns() {
      const hiddenFields = ['id', 'company', 'alertStatus', 'createTime']
      const widthMap = {
        advertiserId: '160',
        balance: '110',
        alertThresholdText: '140',
        accountStatus: '100',
        accountType: '150'
      }
      const minWidthMap = {
        name: '260',
        groupName: '140',
        message: '280'
      }
      this.tableColumns.forEach((column) => {
        if (hiddenFields.indexOf(column.field) !== -1) {
          this.$set(column, 'show', false)
        }
        if (widthMap[column.field]) {
          this.$set(column, 'width', widthMap[column.field])
        }
        if (minWidthMap[column.field]) {
          this.$set(column, 'width', null)
          this.$set(column, 'minWidth', minWidthMap[column.field])
        }
      })
    },
    scheduleTableLayout(delay) {
      if (this.isCardView) {
        return
      }
      const runLayout = () => {
        if (this.$refs.table && this.$refs.table.syncTableLayout) {
          this.$refs.table.syncTableLayout()
        }
      }
      if (delay) {
        setTimeout(() => {
          this.$nextTick(runLayout)
        }, delay)
        return
      }
      this.$nextTick(runLayout)
    },
    getAdvertiserRowId(row) {
      if (!row) {
        return ''
      }
      return row.id || row.advertiserId || ''
    },
    createDefaultAlertForm() {
      return {
        id: '',
        advertiserId: '',
        agentId: '',
        accountName: '',
        ruleName: '',
        balanceMin: '',
        wxNumber: [],
        limitNotice: true,
        interval: 30,
        status: 1,
      }
    },
    hasPermission(permission) {
      return this.groupPermissions.indexOf(permission) !== -1
    },
    getAccountTypeText(accountType) {
      const matchedItem = this.allAccountTypeOptions.find(item => Number(item.key) === Number(accountType))
      return matchedItem ? matchedItem.value : '未知账户类型'
    },
    getNotifyIntervalText(interval) {
      const matchedItem = this.notifyIntervalOptions.find(item => Number(item.key) === Number(interval))
      return matchedItem ? matchedItem.value : ''
    },
    formatContactNames(openIds) {
      if (!Array.isArray(openIds) || !openIds.length) {
        return ''
      }
      return openIds.map((openId) => {
        const contact = this.contactOptions.find(item => item.openId === openId)
        return contact ? contact.wechatName : openId
      }).join('、')
    },
    extractAdvertiserIdFromRule(rule) {
      if (!rule) {
        return ''
      }
      if (rule.monitorAdvertiserId !== undefined && rule.monitorAdvertiserId !== null && rule.monitorAdvertiserId !== '') {
        return String(rule.monitorAdvertiserId)
      }
      const executeContent = rule.executeContent || {}
      if (executeContent.alertAdvertiserId !== undefined && executeContent.alertAdvertiserId !== null && executeContent.alertAdvertiserId !== '') {
        return String(executeContent.alertAdvertiserId)
      }
      if (Array.isArray(rule.monitorObject) && rule.monitorObject.length === 1) {
        return String(rule.monitorObject[0])
      }
      return ''
    },
    isSingleAdvertiserAlertRule(rule) {
      if (!rule || Number(rule.intelligentRegulationType) !== 2) {
        return false
      }
      const executeContent = rule.executeContent || {}
      if (executeContent.singleAdvertiserAlert || executeContent.sourcePage === 'oceanEngineAdvertiser') {
        return true
      }
      return !!this.extractAdvertiserIdFromRule(rule)
    },
    pickPreferredAlertRule(rules) {
      if (!Array.isArray(rules) || !rules.length) {
        return null
      }
      return rules.slice().sort((prevRule, nextRule) => {
        const prevStatusScore = Number(prevRule && prevRule.status) === 1 ? 1 : 0
        const nextStatusScore = Number(nextRule && nextRule.status) === 1 ? 1 : 0
        if (prevStatusScore !== nextStatusScore) {
          return nextStatusScore - prevStatusScore
        }
        const prevTime = new Date(prevRule && prevRule.createTime ? prevRule.createTime : 0).getTime()
        const nextTime = new Date(nextRule && nextRule.createTime ? nextRule.createTime : 0).getTime()
        if (prevTime !== nextTime) {
          return nextTime - prevTime
        }
        return Number(nextRule && nextRule.id ? nextRule.id : 0) - Number(prevRule && prevRule.id ? prevRule.id : 0)
      })[0]
    },
    buildAlertRuleMap(rules) {
      const ruleMap = {}
      rules.forEach((rule) => {
        const advertiserId = this.extractAdvertiserIdFromRule(rule)
        if (!advertiserId) {
          return
        }
        if (!ruleMap[advertiserId]) {
          ruleMap[advertiserId] = []
        }
        ruleMap[advertiserId].push(rule)
      })
      const preferredRuleMap = {}
      Object.keys(ruleMap).forEach((advertiserId) => {
        preferredRuleMap[advertiserId] = this.pickPreferredAlertRule(ruleMap[advertiserId])
      })
      return preferredRuleMap
    },
    legacyGetAlertRule(row) {
      if (!row) {
        return null
      }
      return this.balanceAlertRuleMap[String(row.advertiserId || '')] || null
    },
    legacyHasAlertRule(row) {
      return !!this.getAlertRule(row)
    },
    legacyIsAlertEnabled(row) {
      const alertRule = this.getAlertRule(row)
      return !!(alertRule && Number(alertRule.status) === 1)
    },
    legacyGetAlertStatusText(row) {
      const alertRule = this.getAlertRule(row)
      if (!alertRule) {
        return '未设置预警'
      }
      return Number(alertRule.status) === 1 ? '已启用预警' : '已停用预警'
    },
    legacyGetAlertTagType(row) {
      const alertRule = this.getAlertRule(row)
      if (!alertRule) {
        return 'info'
      }
      return Number(alertRule.status) === 1 ? 'success' : 'warning'
    },
    legacyGetAlertContactSummary(row) {
      const alertRule = this.getAlertRule(row)
      if (!alertRule || !alertRule.noticeConfig) {
        return ''
      }
      return this.formatContactNames(alertRule.noticeConfig.wxNumber)
    },
    legacyGetAlertSummaryText(row) {
      const alertRule = this.getAlertRule(row)
      if (!alertRule || !alertRule.executeContent) {
        return '当前账户还没有配置余额预警。'
      }
      const balanceMin = alertRule.executeContent.balanceMin
      const thresholdText = balanceMin === undefined || balanceMin === null || balanceMin === '' ? '--' : `${balanceMin} 元`
      const interval = alertRule.noticeConfig ? Number(alertRule.noticeConfig.interval || 0) : 0
      const intervalText = interval > 0 ? this.getNotifyIntervalText(interval) : '不限频率'
      return `余额低于 ${thresholdText} 时提醒，${intervalText}。`
    },
    legacyApplyAlertMetadataToRows(rows) {
      if (!Array.isArray(rows)) {
        return
      }
      rows.forEach((row) => {
        const alertRule = this.getAlertRule(row)
        if (!alertRule) {
          this.$set(row, 'alertStatus', -1)
          this.$set(row, 'alertThresholdText', '未设置')
          this.$set(row, 'alertRuleId', '')
          return
        }
        this.$set(row, 'alertStatus', Number(alertRule.status) === 1 ? 1 : 0)
        this.$set(row, 'alertThresholdText', `${alertRule.executeContent && alertRule.executeContent.balanceMin !== undefined ? alertRule.executeContent.balanceMin : '--'}元`)
        this.$set(row, 'alertRuleId', alertRule.id || '')
      })
    },
    legacySyncTableAlertRows() {
      if (this.$refs.table && Array.isArray(this.$refs.table.tableData)) {
        this.applyAlertMetadataToRows(this.$refs.table.tableData)
      }
    },
    legacyHandleAdvertiserTableLoaded(payload) {
      const rows = payload && Array.isArray(payload.rows) ? payload.rows : []
      this.advertiserTableRows = rows
      this.applyAlertMetadataToRows(rows)
      if (!this.balanceAlertRuleList.length && !this.alertRuleLoading) {
        this.fetchAllAlertRules()
      }
    },
    handleAdvertiserTableReset() {
      this.activeGroupFilter = 'all'
      this.scheduleTableLayout(30)
    },
    loadContactOptions(showErrorMessage) {
      if (this.contactLoading) {
        return
      }
      this.contactLoading = true
      this.$axios.get({
        url: '/wechatUser/listByCondition',
        data: {
          pageIndex: 1,
          pageSize: 200
        },
        loading: false,
        success: (res) => {
          if (res.code === 0) {
            const data = res.data || {}
            this.contactOptions = Array.isArray(data.content) ? data.content : (Array.isArray(res.data) ? res.data : [])
          } else if (showErrorMessage) {
            this.$message.error(res.msg || '获取联系人失败')
          }
          this.contactLoading = false
        },
        error: () => {
          this.contactLoading = false
          if (showErrorMessage) {
            this.$message.error('获取联系人失败')
          }
        }
      })
    },
    legacyFetchAllAlertRules(showErrorMessage) {
      if (this.alertRuleLoading) {
        return
      }
      this.alertRuleLoading = true
      const pageSize = 200
      const allRules = []
      const fetchPage = (pageIndex) => {
        this.$axios.get({
          url: '/smartRoiRules/listByCondition',
          data: {
            intelligentRegulationType: 2,
            pageIndex,
            pageSize
          },
          loading: false,
          success: (res) => {
            if (res.code !== 0) {
              this.alertRuleLoading = false
              if (showErrorMessage) {
                this.$message.error(res.msg || '获取余额预警失败')
              }
              return
            }
            const data = res.data || {}
            const content = Array.isArray(data.content) ? data.content : []
            allRules.push(...content.filter(rule => this.isSingleAdvertiserAlertRule(rule)))
            const totalPages = Number(data.totalPages || 1)
            if (pageIndex < totalPages) {
              fetchPage(pageIndex + 1)
              return
            }
            this.balanceAlertRuleList = allRules
            this.balanceAlertRuleMap = this.buildAlertRuleMap(allRules)
            this.applyAlertMetadataToRows(this.advertiserTableRows)
            this.syncTableAlertRows()
            this.alertRuleLoading = false
          },
          error: () => {
            this.alertRuleLoading = false
            if (showErrorMessage) {
              this.$message.error('获取余额预警失败')
            }
          }
        })
      }
      fetchPage(1)
    },
    legacyCanManageAlert(row) {
      if (!this.canOperateAdvertiser(row)) {
        return false
      }
      if (this.hasAlertRule(row)) {
        return this.hasPermission('smartRoiRules:update')
      }
      return this.hasPermission('smartRoiRules:add')
    },
    legacyBuildDefaultAlertRuleName(row) {
      const baseName = row && row.name ? row.name : `广告主${row && row.advertiserId ? row.advertiserId : ''}`
      return `${baseName}-余额预警`
    },
    openAlertDialog(row) {
      if (!row) {
        return
      }
      const alertRule = this.getAlertRule(row)
      this.currentAlertRow = row
      this.loadContactOptions()
      if (alertRule) {
        this.alertForm = {
          id: alertRule.id,
          advertiserId: row.advertiserId,
          agentId: row.agentId,
          accountName: row.name,
          ruleName: alertRule.ruleName || this.buildDefaultAlertRuleName(row),
          balanceMin: alertRule.executeContent && alertRule.executeContent.balanceMin !== undefined ? String(alertRule.executeContent.balanceMin) : '',
          wxNumber: alertRule.noticeConfig && Array.isArray(alertRule.noticeConfig.wxNumber) ? alertRule.noticeConfig.wxNumber.slice() : [],
          limitNotice: !!(alertRule.noticeConfig && Number(alertRule.noticeConfig.interval) > 0),
          interval: alertRule.noticeConfig && Number(alertRule.noticeConfig.interval) > 0 ? Number(alertRule.noticeConfig.interval) : 30,
          status: Number(alertRule.status) === 1 ? 1 : 0,
        }
      } else {
        this.alertForm = {
          ...this.createDefaultAlertForm(),
          advertiserId: row.advertiserId,
          agentId: row.agentId,
          accountName: row.name,
          ruleName: this.buildDefaultAlertRuleName(row),
        }
      }
      this.alertDialogVisible = true
      this.$nextTick(() => {
        if (this.$refs.alertFormRef) {
          this.$refs.alertFormRef.clearValidate()
        }
      })
    },
    resetAlertDialog() {
      this.alertDialogSubmitting = false
      this.currentAlertRow = null
      this.alertForm = this.createDefaultAlertForm()
      this.$nextTick(() => {
        if (this.$refs.alertFormRef) {
          this.$refs.alertFormRef.clearValidate()
        }
      })
    },
    legacyBuildAlertRequestPayload() {
      const advertiserId = this.currentAlertRow ? this.currentAlertRow.advertiserId : this.alertForm.advertiserId
      return {
        id: this.alertForm.id || undefined,
        userId: this.alertForm.agentId,
        ruleName: this.alertForm.ruleName,
        marketingTarget: 0,
        monitorType: 1,
        status: this.alertForm.status,
        monitorObject: JSON.stringify([Number(advertiserId)]),
        planWhitelist: JSON.stringify([]),
        executeContent: JSON.stringify({
          balanceMin: Number(this.alertForm.balanceMin),
          sourcePage: 'oceanEngineAdvertiser',
          singleAdvertiserAlert: true,
          alertAdvertiserId: Number(advertiserId),
        }),
        noticeConfig: JSON.stringify({
          wxNumber: this.alertForm.wxNumber,
          interval: this.alertForm.limitNotice ? Number(this.alertForm.interval) : 0
        }),
        intelligentRegulationType: 2,
        monitorAdvertiserId: String(advertiserId)
      }
    },
    legacySubmitAlertForm() {
      if (!this.currentAlertRow) {
        return
      }
      this.$refs.alertFormRef.validate((valid) => {
        if (!valid) {
          return
        }
        this.alertDialogSubmitting = true
        const payload = this.buildAlertRequestPayload()
        this.$axios.post({
          url: payload.id ? '/smartRoiRules/update' : '/smartRoiRules/add',
          data: payload,
          success: (res) => {
            if (res.code === 0) {
              this.$message.success(payload.id ? '余额预警更新成功' : '余额预警创建成功')
              this.alertDialogVisible = false
              this.fetchAllAlertRules(true)
            } else {
              this.$message.error(res.msg || (payload.id ? '余额预警更新失败' : '余额预警创建失败'))
            }
            this.alertDialogSubmitting = false
          },
          error: () => {
            this.alertDialogSubmitting = false
            this.$message.error(payload.id ? '余额预警更新失败' : '余额预警创建失败')
          }
        })
      })
    },
    legacyUpdateAlertRuleStatus(alertRule, nextStatus, successMessage) {
      if (!alertRule) {
        return
      }
      const advertiserId = this.extractAdvertiserIdFromRule(alertRule)
      const payload = {
        id: alertRule.id,
        userId: alertRule.userId,
        ruleName: alertRule.ruleName,
        marketingTarget: alertRule.marketingTarget,
        monitorType: alertRule.monitorType,
        status: nextStatus,
        monitorObject: JSON.stringify(Array.isArray(alertRule.monitorObject) ? alertRule.monitorObject : []),
        planWhitelist: JSON.stringify(Array.isArray(alertRule.planWhitelist) ? alertRule.planWhitelist : []),
        executeContent: JSON.stringify({
          ...(alertRule.executeContent || {}),
          sourcePage: 'oceanEngineAdvertiser',
          singleAdvertiserAlert: true,
          alertAdvertiserId: advertiserId ? Number(advertiserId) : undefined,
        }),
        noticeConfig: JSON.stringify({
          wxNumber: alertRule.noticeConfig && Array.isArray(alertRule.noticeConfig.wxNumber) ? alertRule.noticeConfig.wxNumber : [],
          interval: alertRule.noticeConfig ? Number(alertRule.noticeConfig.interval || 0) : 0
        }),
        intelligentRegulationType: alertRule.intelligentRegulationType || 2,
        monitorAdvertiserId: advertiserId
      }
      this.$axios.post({
        url: '/smartRoiRules/update',
        data: payload,
        success: (res) => {
          if (res.code === 0) {
            this.$message.success(successMessage)
            this.fetchAllAlertRules(true)
          } else {
            this.$message.error(res.msg || '预警状态更新失败')
          }
        },
        error: () => {
          this.$message.error('预警状态更新失败')
        }
      })
    },
    legacyToggleAlertStatus(row) {
      const alertRule = this.getAlertRule(row)
      if (!alertRule) {
        this.openAlertDialog(row)
        return
      }
      const nextStatus = Number(alertRule.status) === 1 ? 0 : 1
      const actionText = nextStatus === 1 ? '启用' : '停用'
      this.$confirm(`确认${actionText}账户「${row.name || row.advertiserId}」的余额预警吗？`, '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        this.updateAlertRuleStatus(alertRule, nextStatus, `${actionText}余额预警成功`)
      }).catch(() => { })
    },
    getAlertRule(row) {
      if (!row || (!row.alertConfigured && !row.alertRuleId)) {
        return null
      }
      return {
        id: row.alertRuleId,
        ruleName: row.alertRuleName || this.buildDefaultAlertRuleName(row),
        status: row.alertEnabled ? 1 : 0,
        executeContent: {
          balanceMin: row.alertThreshold
        },
        noticeConfig: {
          wxNumber: Array.isArray(row.alertWxNumberList) ? row.alertWxNumberList : [],
          interval: row.alertIntervalMinutes === undefined || row.alertIntervalMinutes === null ? 0 : Number(row.alertIntervalMinutes)
        }
      }
    },
    hasAlertRule(row) {
      return !!this.getAlertRule(row)
    },
    isAlertEnabled(row) {
      return !!(row && row.alertEnabled)
    },
    getAlertStatusText(row) {
      if (!this.hasAlertRule(row)) {
        return '未设置预警'
      }
      return this.isAlertEnabled(row) ? '已启用预警' : '已停用预警'
    },
    getAlertTagType(row) {
      if (!this.hasAlertRule(row)) {
        return 'info'
      }
      return this.isAlertEnabled(row) ? 'success' : 'warning'
    },
    getAlertContactSummary(row) {
      const contacts = row && Array.isArray(row.alertWxNumberList) ? row.alertWxNumberList : []
      return this.formatContactNames(contacts)
    },
    getAlertSummaryText(row) {
      const alertRule = this.getAlertRule(row)
      if (!alertRule || !alertRule.executeContent) {
        return '当前账户还没有配置余额预警。'
      }
      const balanceMin = alertRule.executeContent.balanceMin
      const thresholdText = balanceMin === undefined || balanceMin === null || balanceMin === '' ? '--' : `${balanceMin} 元`
      const interval = alertRule.noticeConfig ? Number(alertRule.noticeConfig.interval || 0) : 0
      const intervalText = interval > 0 ? this.getNotifyIntervalText(interval) : '不限频率'
      return `余额低于 ${thresholdText} 时提醒，${intervalText}。`
    },
    applyAlertMetadataToRows(rows) {
      if (!Array.isArray(rows)) {
        return
      }
      rows.forEach((row) => {
        const alertRule = this.getAlertRule(row)
        if (!alertRule) {
          this.$set(row, 'alertConfigured', false)
          this.$set(row, 'alertEnabled', false)
          this.$set(row, 'alertStatus', -1)
          this.$set(row, 'alertThresholdText', '未设置')
          this.$set(row, 'alertRuleId', '')
          return
        }
        this.$set(row, 'alertConfigured', true)
        this.$set(row, 'alertStatus', this.isAlertEnabled(row) ? 1 : 0)
        this.$set(row, 'alertThresholdText', `${alertRule.executeContent && alertRule.executeContent.balanceMin !== undefined ? alertRule.executeContent.balanceMin : '--'}元`)
        this.$set(row, 'alertRuleId', alertRule.id || '')
      })
    },
    applyAlertConfigToRow(row, config) {
      if (!row || !config) {
        return
      }
      this.$set(row, 'alertConfigured', !!config.alertConfigured)
      this.$set(row, 'alertRuleId', config.alertRuleId || '')
      this.$set(row, 'alertRuleName', config.alertRuleName || '')
      this.$set(row, 'alertEnabled', !!config.alertEnabled)
      this.$set(row, 'alertThreshold', config.alertThreshold)
      this.$set(row, 'alertWxNumberList', Array.isArray(config.alertWxNumberList) ? config.alertWxNumberList : [])
      this.$set(row, 'alertIntervalMinutes', config.alertIntervalMinutes)
      this.applyAlertMetadataToRows([row])
    },
    syncTableAlertRows() {
      if (this.$refs.table && Array.isArray(this.$refs.table.tableData)) {
        this.applyAlertMetadataToRows(this.$refs.table.tableData)
      }
    },
    handleAdvertiserTableLoaded(payload) {
      const rows = payload && Array.isArray(payload.rows) ? payload.rows : []
      this.advertiserTableRows = rows
      this.applyAlertMetadataToRows(rows)
      this.scheduleTableLayout()
      this.refreshGroupStatistics(false, false)
    },
    fetchAllAlertRules() {
      if (this.$refs.table && this.$refs.table.getData) {
        this.$refs.table.getData()
      }
    },
    canManageAlert(row) {
      if (!this.canOperateAdvertiser(row)) {
        return false
      }
      return this.hasPermission('oceanEngineAdvertiser:update') || this.hasPermission('smartRoiRules:update') || this.hasPermission('smartRoiRules:add')
    },
    buildDefaultAlertRuleName(row) {
      const baseName = row && row.name ? row.name : `广告主${row && row.advertiserId ? row.advertiserId : ''}`
      return `${baseName}-余额预警`
    },
    buildAlertRequestPayload() {
      const advertiserId = this.currentAlertRow ? this.currentAlertRow.advertiserId : this.alertForm.advertiserId
      return {
        advertiserId: Number(advertiserId),
        alertRuleId: this.alertForm.id || undefined,
        alertEnabled: Number(this.alertForm.status) === 1,
        alertThreshold: Number(this.alertForm.balanceMin),
        alertWxNumberList: this.alertForm.wxNumber,
        alertIntervalMinutes: this.alertForm.limitNotice ? Number(this.alertForm.interval) : 0
      }
    },
    submitAlertForm() {
      if (!this.currentAlertRow) {
        return
      }
      this.$refs.alertFormRef.validate((valid) => {
        if (!valid) {
          return
        }
        this.alertDialogSubmitting = true
        const payload = this.buildAlertRequestPayload()
        this.$axios.post({
          url: '/oceanEngineAdvertiser/singleAdvertiserBalanceAlert/save',
          data: payload,
          success: (res) => {
            if (res.code === 0) {
              this.$message.success(payload.alertRuleId ? '余额预警更新成功' : '余额预警创建成功')
              this.alertDialogVisible = false
              this.applyAlertConfigToRow(this.currentAlertRow, res.data)
              this.fetchAllAlertRules()
            } else {
              this.$message.error(res.msg || (payload.alertRuleId ? '余额预警更新失败' : '余额预警创建失败'))
            }
            this.alertDialogSubmitting = false
          },
          error: () => {
            this.alertDialogSubmitting = false
            this.$message.error(payload.alertRuleId ? '余额预警更新失败' : '余额预警创建失败')
          }
        })
      })
    },
    updateAlertRuleStatus(row, nextStatus, successMessage) {
      if (!row) {
        return
      }
      this.$axios.post({
        url: '/oceanEngineAdvertiser/singleAdvertiserBalanceAlert/toggle',
        data: {
          advertiserId: Number(row.advertiserId),
          alertEnabled: nextStatus === 1
        },
        success: (res) => {
          if (res.code === 0) {
            this.$message.success(successMessage)
            this.applyAlertConfigToRow(row, res.data)
            this.fetchAllAlertRules()
          } else {
            this.$message.error(res.msg || '预警状态更新失败')
          }
        },
        error: () => {
          this.$message.error('预警状态更新失败')
        }
      })
    },
    toggleAlertStatus(row) {
      const alertRule = this.getAlertRule(row)
      if (!alertRule) {
        this.openAlertDialog(row)
        return
      }
      const nextStatus = Number(alertRule.status) === 1 ? 0 : 1
      const actionText = nextStatus === 1 ? '启用' : '停用'
      this.$confirm(`确认${actionText}账户「${row.name || row.advertiserId}」的余额预警吗？`, '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        this.updateAlertRuleStatus(row, nextStatus, `${actionText}余额预警成功`)
      }).catch(() => { })
    },
    refreshAdvertiserBalance(row) {
      const advertiserRowId = this.getAdvertiserRowId(row)
      if (!advertiserRowId) {
        this.$message.error('未获取到账户标识')
        return
      }
      this.$axios.post({
        url: '/oceanEngineAdvertiser/search/balance/' + advertiserRowId,
        success: (res) => {
          if (res.code === 0) {
            this.$set(row, 'balance', res.data)
            this.refreshGroupStatistics(false, true)
          } else {
            this.$message.error(res.msg)
          }
        }
      })
    },
    updateAdvertiserInfo(row) {
      const advertiserRowId = this.getAdvertiserRowId(row)
      if (!advertiserRowId) {
        this.$message.error('未获取到账户标识')
        return
      }
      this.$axios.get({
        url: '/oceanEngineAdvertiser/updateAdvertiserInfoStatus/' + advertiserRowId,
        success: (res) => {
          if (res.code === 0) {
            this.$refs.table.getData()
          } else {
            this.$message.error(res.msg)
          }
        }
      })
    },
    goAddContrats() {
      this.alertDialogVisible = false
      setTimeout(() => {
        this.$router.push({
          path: '/qdd/admin/contacts.html',
        })
      }, 1)
    },
    initAccountTypeConfig() {
      const searchAccountType = this.searchs.find(item => item.name === 'accountType')
      if (searchAccountType) {
        searchAccountType.selectValue = this.supportedAccountTypeOptions
      }

      const tableAccountType = this.tableColumns.find(item => item.field === 'accountType')
      if (tableAccountType) {
        tableAccountType.selectData = this.allAccountTypeOptions.map(item => ({ id: item.key, name: item.value }))
      }
    },
    initGroupConfig() {
      this.refreshGroupOptions()
    },
    loadGroupPermissions(showErrorMessage) {
      this.$axios.get({
        url: '/permission/getUserPermissions',
        success: (res) => {
          if (res.code === 0) {
            this.groupPermissions = res.data || []
          } else if (showErrorMessage) {
            this.$message.error(res.msg || '获取分组权限失败')
          }
        },
        error: () => {
          if (showErrorMessage) {
            this.$message.error('获取分组权限失败')
          }
        }
      })
    },
    loadAgentOptions(showErrorMessage) {
      this.$axios.post({
        url: '/agent/getAll',
        success: (res) => {
          if (res.code === 0) {
            const sourceList = (res.data && res.data.data) || res.data || []
            this.agentOptions = sourceList.map(item => ({
              id: item.id,
              name: item.name || item.username || `代理${item.id}`
            }))
          } else if (showErrorMessage) {
            this.$message.error(res.msg || '获取代理列表失败')
          }
        },
        error: () => {
          if (showErrorMessage) {
            this.$message.error('获取代理列表失败')
          }
        }
      })
    },
    hasGroupPermission(permission) {
      return this.groupPermissions.indexOf(permission) !== -1
    },
    getAgentName(agentId) {
      const matchedAgent = this.agentOptions.find(option => option.id === agentId)
      return matchedAgent ? matchedAgent.name : `代理ID ${agentId || '--'}`
    },
    normalizeGroupStatsKey(groupId) {
      if (groupId === undefined || groupId === null || groupId === '') {
        return '0'
      }
      if (this.isGroupKeyEqual(groupId, 'all')) {
        return 'all'
      }
      return String(groupId)
    },
    parseBalanceValue(balance) {
      if (balance === undefined || balance === null || balance === '') {
        return 0
      }
      const parsedValue = Number(String(balance).replace(/,/g, ''))
      return Number.isFinite(parsedValue) ? parsedValue : 0
    },
    formatBalanceValue(balance) {
      return this.parseBalanceValue(balance).toFixed(2)
    },
    formatMetricAmount(value) {
      return this.parseBalanceValue(value).toFixed(2)
    },
    formatMetricCount(value) {
      const parsedValue = Number(value)
      return Number.isFinite(parsedValue) ? Math.max(0, Math.round(parsedValue)) : 0
    },
    formatMetricRoi(value) {
      const parsedValue = Number(value)
      return Number.isFinite(parsedValue) ? parsedValue.toFixed(2) : '0.00'
    },
    getGroupStatsById(groupId) {
      const normalizedKey = this.normalizeGroupStatsKey(groupId)
      return this.groupStatsMap[normalizedKey] || { accountCount: 0, totalBalance: 0 }
    },
    getGroupAccountCount(groupId) {
      return this.getGroupStatsById(groupId).accountCount
    },
    getGroupTotalBalanceText(groupId) {
      return this.formatBalanceValue(this.getGroupStatsById(groupId).totalBalance)
    },
    clearGroupStatsRefreshTimer() {
      if (this.groupStatsRefreshTimer) {
        clearTimeout(this.groupStatsRefreshTimer)
        this.groupStatsRefreshTimer = null
      }
    },
    scheduleGroupStatsRefreshPoll(showErrorMessage) {
      this.clearGroupStatsRefreshTimer()
      this.groupStatsRefreshTimer = setTimeout(() => {
        this.groupStatsRefreshTimer = null
        this.refreshGroupStatistics(showErrorMessage, false, true)
      }, 1500)
    },
    buildGroupStatisticsQuery() {
      const tableQuery = this.$refs.table && this.$refs.table.query ? { ...this.$refs.table.query } : {}
      delete tableQuery.pageIndex
      delete tableQuery.pageSize
      delete tableQuery.total
      delete tableQuery.totalPages
      tableQuery.groupId = ''
      return tableQuery
    },
    buildGroupStatisticsSignature(query) {
      const normalizedQuery = {}
      Object.keys(query || {}).sort().forEach((key) => {
        const value = query[key]
        if (value !== undefined && value !== null && value !== '') {
          normalizedQuery[key] = value
        }
      })
      return JSON.stringify(normalizedQuery)
    },
    flushPendingGroupStatsRequest() {
      if (!this.pendingGroupStatsRequest) {
        return
      }
      const pendingRequest = this.pendingGroupStatsRequest
      this.pendingGroupStatsRequest = null
      this.refreshGroupStatistics(
        pendingRequest.showErrorMessage,
        pendingRequest.forceRefresh,
        pendingRequest.allowDuplicateQuery
      )
    },
    refreshGroupStatistics(showErrorMessage, forceRefresh, allowDuplicateQuery) {
      const statsQuery = this.buildGroupStatisticsQuery()
      const querySignature = this.buildGroupStatisticsSignature(statsQuery)
      const hasStatsSnapshot = !!Object.keys(this.groupStatsMap || {}).length
      if (!forceRefresh && !allowDuplicateQuery && hasStatsSnapshot && this.groupStatsQuerySignature === querySignature) {
        return
      }
      if (this.groupStatsLoading) {
        if (forceRefresh || this.groupStatsQuerySignature !== querySignature) {
          this.pendingGroupStatsRequest = {
            showErrorMessage,
            forceRefresh: !!forceRefresh,
            allowDuplicateQuery: !!allowDuplicateQuery
          }
        }
        return
      }
      this.groupStatsLoading = true
      this.$axios.get({
        url: '/oceanEngineAdvertiser/groupStatisticsAsync',
        data: Object.assign({}, statsQuery, {
          forceRefresh: !!forceRefresh
        }),
        loading: false,
        success: (res) => {
          this.groupStatsLoading = false
          if (res.code === 0) {
            const snapshot = res.data || {}
            this.groupStatsMap = snapshot.statsMap || {}
            this.groupStatsQuerySignature = querySignature
            if (snapshot.refreshing || !snapshot.ready) {
              this.scheduleGroupStatsRefreshPoll(showErrorMessage)
            } else {
              this.clearGroupStatsRefreshTimer()
            }
          } else if (showErrorMessage) {
            this.$message.error(res.msg || '获取分组统计失败')
          }
          this.flushPendingGroupStatsRequest()
        },
        error: () => {
          this.groupStatsLoading = false
          this.clearGroupStatsRefreshTimer()
          if (showErrorMessage) {
            this.$message.error('获取分组统计失败')
          }
          this.flushPendingGroupStatsRequest()
        }
      })
    },
    buildGroupSearchOptions(groups) {
      const searchOptions = [this.ungroupedOption]
      groups.forEach((group) => {
        if (!group || group.id === undefined || group.id === null) {
          return
        }
        searchOptions.push({
          key: group.id,
          value: group.name
        })
      })
      return searchOptions
    },
    buildGroupSelectData(groups) {
      const selectData = []
      groups.forEach((group) => {
        if (!group || group.id === undefined || group.id === null) {
          return
        }
        selectData.push({
          id: group.id,
          name: group.name
        })
      })
      return selectData
    },
    applyGroupOptions(groups) {
      const searchField = this.searchs.find(item => item.name === 'groupId')
      if (searchField) {
        searchField.selectValue = this.buildGroupSearchOptions(groups)
      }

      const formField = this.formFields.model.find(item => item.field === 'groupId')
      if (formField) {
        formField.selectData = this.buildGroupSelectData(groups)
      }
    },
    refreshGroupOptions(showErrorMessage, forceStatsRefresh) {
      this.groupListLoading = true
      this.$axios.post({
        url: '/oceanEngineAdvertiserGroup/getAll',
        success: (res) => {
          if (res.code === 0) {
            const groups = (res.data && res.data.data) || res.data || []
            this.groupList = groups
            this.applyGroupOptions(groups)
            this.syncActiveGroupFilter(groups)
            this.refreshGroupStatistics(false, !!forceStatsRefresh)
          } else if (showErrorMessage) {
            this.$message.error(res.msg || '获取分组列表失败')
          }
          this.groupListLoading = false
        },
        error: () => {
          if (showErrorMessage) {
            this.$message.error('获取分组列表失败')
          }
          this.groupListLoading = false
        }
      })
    },
    canOperateAdvertiser(row) {
      return !(row && row.readOnly)
    },
    isGroupKeyEqual(firstKey, secondKey) {
      return String(firstKey) === String(secondKey)
    },
    isGroupFilterActive(groupKey) {
      return this.isGroupKeyEqual(this.activeGroupFilter, groupKey)
    },
    handleSidebarGroupSelect(groupKey) {
      this.activeGroupFilter = groupKey
      const selectedGroupId = this.isGroupKeyEqual(groupKey, 'all') ? '' : groupKey
      if (this.$refs.table) {
        this.$set(this.$refs.table.query, 'groupId', selectedGroupId)
        this.$refs.table.handleSearch()
      }
    },
    syncActiveGroupFilter(groups) {
      if (this.isGroupFilterActive('all') || this.isGroupFilterActive(0)) {
        return
      }
      const stillExists = groups.some(group => this.isGroupKeyEqual(group.id, this.activeGroupFilter))
      if (!stillExists) {
        this.handleSidebarGroupSelect('all')
      }
    },
    openGroupDialog(mode, group) {
      this.groupDialogVisible = true
      if (mode === 'edit' && group) {
        this.prepareEditGroup(group)
        return
      }
      this.prepareCreateGroup()
    },
    prepareCreateGroup() {
      this.groupEditorForm = {
        id: '',
        agentId: '',
        name: ''
      }
      this.$nextTick(() => {
        if (this.$refs.groupEditorForm) {
          this.$refs.groupEditorForm.clearValidate()
        }
      })
    },
    prepareEditGroup(group) {
      this.groupEditorForm = {
        id: group.id,
        agentId: group.agentId,
        name: group.name
      }
      this.$nextTick(() => {
        if (this.$refs.groupEditorForm) {
          this.$refs.groupEditorForm.clearValidate()
        }
      })
    },
    isEditingGroup(groupId) {
      return this.isGroupKeyEqual(this.groupEditorForm.id, groupId)
    },
    submitGroupForm() {
      if (!this.canAddGroup && !this.groupEditorForm.id) {
        this.$message.warning('暂无新增分组权限')
        return
      }
      if (!this.canUpdateGroup && this.groupEditorForm.id) {
        this.$message.warning('暂无修改分组权限')
        return
      }
      this.$refs.groupEditorForm.validate((valid) => {
        if (!valid) {
          return
        }
        this.groupSubmitting = true
        const isUpdate = !!this.groupEditorForm.id
        const payload = {
          agentId: this.groupEditorForm.agentId,
          name: (this.groupEditorForm.name || '').trim()
        }
        if (isUpdate) {
          payload.id = this.groupEditorForm.id
        }
        this.$axios.post({
          url: isUpdate ? '/oceanEngineAdvertiserGroup/update' : '/oceanEngineAdvertiserGroup/add',
          data: payload,
          success: (res) => {
            if (res.code === 0) {
              this.$message.success(isUpdate ? '分组更新成功' : '分组创建成功')
              this.prepareCreateGroup()
              this.refreshGroupOptions(true, true)
            } else {
              this.$message.error(res.msg || (isUpdate ? '分组更新失败' : '分组创建失败'))
            }
            this.groupSubmitting = false
          },
          error: () => {
            this.groupSubmitting = false
            this.$message.error(isUpdate ? '分组更新失败' : '分组创建失败')
          }
        })
      })
    },
    deleteGroup(group) {
      if (!this.canDeleteGroup) {
        this.$message.warning('暂无删除分组权限')
        return
      }
      this.$confirm(`确认删除分组“${group.name}”吗？`, '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        this.groupDeleteLoadingId = group.id
        this.$axios.post({
          url: `/oceanEngineAdvertiserGroup/delete/${group.id}`,
          success: (res) => {
            if (res.code === 0) {
              this.$message.success('分组删除成功')
              if (this.isEditingGroup(group.id)) {
                this.prepareCreateGroup()
              }
              this.refreshGroupOptions(true, true)
            } else {
              this.$message.error(res.msg || '分组删除失败')
            }
            this.groupDeleteLoadingId = null
          },
          error: () => {
            this.groupDeleteLoadingId = null
            this.$message.error('分组删除失败')
          }
        })
      }).catch(() => { })
    },
    openBatchGroupDialog() {
      const selectedIds = (this.$refs.table && this.$refs.table.selectedIds) ? this.$refs.table.selectedIds.slice() : []
      if (!selectedIds.length) {
        this.$message.warning('请先勾选需要批量处理的账户')
        return
      }
      this.batchGroupSelectionIds = selectedIds
      this.batchGroupDialogVisible = true
      this.$nextTick(() => {
        if (this.$refs.batchGroupForm) {
          this.$refs.batchGroupForm.clearValidate()
        }
      })
    },
    resetBatchGroupDialog() {
      this.batchGroupSubmitting = false
      this.batchGroupSelectionIds = []
      this.batchGroupForm = {
        groupId: ''
      }
      this.$nextTick(() => {
        if (this.$refs.batchGroupForm) {
          this.$refs.batchGroupForm.clearValidate()
        }
      })
    },
    submitBatchGroup() {
      if (!this.batchGroupSelectionIds.length) {
        this.$message.warning('请先勾选需要批量处理的账户')
        return
      }
      this.$refs.batchGroupForm.validate((valid) => {
        if (!valid) {
          return
        }
        this.batchGroupSubmitting = true
        const payload = {
          ids: this.batchGroupSelectionIds.join(','),
          groupId: this.batchGroupForm.groupId === '__CLEAR__' ? null : this.batchGroupForm.groupId
        }
        this.$axios.post({
          url: '/oceanEngineAdvertiser/batchUpdateGroup',
          data: payload,
          success: (res) => {
            if (res.code === 0) {
              this.$message.success('批量修改分组成功')
              this.batchGroupDialogVisible = false
              if (this.$refs.table && this.$refs.table.$refs && this.$refs.table.$refs.multipleTable) {
                this.$refs.table.$refs.multipleTable.clearSelection()
              }
              this.$refs.table.getData()
              this.refreshGroupStatistics(false, true)
            } else {
              this.$message.error(res.msg || '批量修改分组失败')
            }
            this.batchGroupSubmitting = false
          },
          error: () => {
            this.batchGroupSubmitting = false
            this.$message.error('批量修改分组失败')
          }
        })
      })
    },
    handleGroupDialogClosed() {
      this.groupManageKeyword = ''
      this.prepareCreateGroup()
      this.refreshGroupOptions()
      if (this.$refs.table) {
        this.$refs.table.getData()
      }
    },
    getAgentId() {
      this.showAccountAuthorize = true
      this.$axios.get({
        url: `/system/getUserAgentId`,
        success: (res) => {
          console.log(res, 11111111111)
          if (res.code === 0) {
            console.log(res, 11111111111)
            if (!res.data) {
              this.$message.error('管理员账户无法生成授权链接');
            } else {
              this.copyLink = 'https://qc.quqianchuan.com/shareAuthorizeLink?agentId=' + res.data
            }

          } else {
            this.$message.error('获取授权链接失败');
          }
        }
      })
    },
    goJumpLink() {
      if (!this.copyLink) {
        this.$message.error('授权链接不存在');

      }
      this.showAccountAuthorize = false
      const a = document.createElement('a')
      a.target = '_blank'
      a.href = this.copyLink
      document.body.appendChild(a)
      a.click();
      document.body.removeChild(a);
    },
    goCopyeLink() {
      if (!this.copyLink) {
        this.$message.error('授权链接不存在');

      }
      this.showAccountAuthorize = false
      if (window.clipboardData) {
        window.clipboardData.setData('text', this.copyLink);
      } else {
        (function (content) {
          document.oncopy = function (e) {
            e.clipboardData.setData('text', content);
            e.preventDefault();
            document.oncopy = null;
          }
        })(this.copyLink);
        document.execCommand('Copy');
      }
    },
    fetchAuthorizationList() {
      this.$axios.get({
        url: `/oceanEngineAdvertiser/aweMeAuthList?id=${this.selectedId}&pageSize=${this.pageSize}&pageIndex=${this.currentPage}`,
        success: (res) => {
          console.log(res, 11111111111)
          if (res.code === 0) {
            this.authorizationInfos = res.data.data.authorization_infos;
            // 根据后端返回数据结构，这里应从page_info获取total_num
            this.totalCount = res.data.data.page_info.total_num;
          } else {
            this.$message.error('获取授权列表失败');
          }
        }
      })
    },
    handleSizeChange(newSize) {
      this.pageSize = newSize;
      this.currentPage = 1;
      this.fetchAuthorizationList();
    },
    // 点击头像放大函数
    handleAvatarClick(url) {
      this.currentAvatarUrl = url;
      this.isImagePopupVisible = true;
    },
    handleCurrentChange(newPage) {
      this.currentPage = newPage;
      this.fetchAuthorizationList();
    },
    // 点击放大头像区域关闭放大层函数
    handleImagePopupClose() {
      this.isImagePopupVisible = false;
    },
    formatAuthType(row, column, cellValue) {
      const typeMap = {
        "OFFICIAL": "官方授权",
        "AWEME_COOPERATOR": "合作达人",
        "SELF": "自运营"
      };
      return cellValue.map(type => typeMap[type]).join(', ');
    },
    formatAuthStatus(row, column, cellValue) {
      const statusMap = {
        "EFFECTIVE": "授权生效",
        "WAIT_BIND": "待达人确认授权",
        "EXPIRED": "授权失效"
      };
      return statusMap[cellValue] || cellValue;
    },
    //重置表单
    resetForm(formName) {
      this.$refs[formName].resetFields();
    },
    submitBatch(formName) {
      // console.log(this.batchForm, "123123")
      this.$refs[formName].validate((valid) => {
        if (valid) {
          this.$axios.post({
            url: '/oceanEngineAdvertiser/refreshToken/',
            data: this.updateForm,
            success: (res) => {
              console.log(res)
              if (res.code === 0) {
                this.$refs.table.getData()
                this.updateVisible = false;
              } else {
                this.$message.error(res.msg);
              }
            }
          });
        }
      })
    },
  }
}
</script>

<style scoped>
.advertiser-page {
  min-height: 100%;
  background: linear-gradient(180deg, #f5f8fd 0%, #f8fbff 100%);
  padding: 14px;
  box-sizing: border-box;
}

.advertiser-layout {
  display: flex;
  gap: 14px;
  align-items: stretch;
  min-width: 0;
}

.group-sidebar {
  width: 286px;
  flex-shrink: 0;
  border-radius: 18px;
  background: linear-gradient(180deg, #ffffff 0%, #f8fbff 100%);
  box-shadow: 0 10px 28px rgba(31, 42, 68, 0.08);
  border: 1px solid rgba(103, 139, 255, 0.08);
  padding: 16px 14px;
  box-sizing: border-box;
}

.group-sidebar__header {
  padding-bottom: 18px;
  border-bottom: 1px solid #eef3fb;
}

.group-sidebar__eyebrow {
  color: #7a8aa0;
  font-size: 12px;
  letter-spacing: 1px;
}

.group-sidebar__title {
  margin-top: 6px;
  font-size: 18px;
  font-weight: 700;
  color: #1f2a44;
}

.group-sidebar__desc {
  margin-top: 6px;
  line-height: 1.5;
  font-size: 12px;
  color: #6f7d93;
}

.group-sidebar__actions {
  margin-top: 16px;
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}

.group-sidebar__summary {
  margin-top: 18px;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
}

.group-sidebar__summary-item {
  padding: 10px 10px;
  border-radius: 12px;
  background: #f4f8ff;
  border: 1px solid #e8eefb;
}

.group-sidebar__summary-label {
  display: block;
  font-size: 12px;
  color: #7f8ba1;
}

.group-sidebar__summary-item strong {
  display: block;
  margin-top: 6px;
  font-size: 16px;
  color: #1f2a44;
}

.group-sidebar__search {
  margin-top: 18px;
}

.group-sidebar__list {
  margin-top: 16px;
  max-height: calc(100vh - 280px);
  overflow-y: auto;
  padding-right: 4px;
}

.group-sidebar__item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 10px;
  border-radius: 12px;
  border: 1px solid transparent;
  background: #ffffff;
  cursor: pointer;
  transition: all .2s ease;
}

.group-sidebar__item + .group-sidebar__item {
  margin-top: 10px;
}

.group-sidebar__item:hover {
  border-color: #cfe0ff;
  background: #f7faff;
}

.group-sidebar__item.is-active {
  background: linear-gradient(135deg, #edf5ff 0%, #f8fbff 100%);
  border-color: #8fb6ff;
  box-shadow: 0 10px 24px rgba(86, 130, 255, 0.12);
}

.group-sidebar__item-icon {
  width: 34px;
  height: 34px;
  border-radius: 10px;
  background: #eef4ff;
  color: #4e82ff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 15px;
  flex-shrink: 0;
}

.group-sidebar__item-content {
  flex: 1;
  min-width: 0;
}

.group-sidebar__item-title {
  font-size: 14px;
  font-weight: 600;
  color: #23314d;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.group-sidebar__item-meta {
  margin-top: 5px;
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
}

.group-sidebar__item-stat {
  display: inline-flex;
  align-items: center;
  padding: 2px 7px;
  border-radius: 999px;
  font-size: 11px;
  line-height: 1.4;
  color: #5f6f8b;
  background: #f4f7fd;
}

.group-sidebar__item-arrow {
  color: #9ea9bc;
  font-size: 12px;
}

.group-sidebar__empty {
  margin-top: 18px;
  padding: 28px 18px;
  border-radius: 18px;
  border: 1px dashed #d6e1f5;
  background: #f9fbff;
  text-align: center;
  color: #7b889d;
}

.group-sidebar__empty i {
  font-size: 28px;
  color: #95a7c2;
}

.group-sidebar__empty-title {
  margin-top: 12px;
  font-size: 15px;
  font-weight: 600;
  color: #2c3b57;
}

.group-sidebar__empty-desc {
  margin-top: 6px;
  line-height: 1.6;
  font-size: 12px;
}

.advertiser-main {
  flex: 1;
  min-width: 0;
}

.advertiser-main__hero {
  display: flex;
  justify-content: space-between;
  gap: 16px;
  align-items: flex-start;
  flex-wrap: wrap;
  padding: 18px 20px;
  border-radius: 18px;
  background: linear-gradient(135deg, #ffffff 0%, #f5f9ff 100%);
  box-shadow: 0 10px 28px rgba(31, 42, 68, 0.08);
  border: 1px solid rgba(103, 139, 255, 0.08);
}

.advertiser-main__hero > div:first-child {
  flex: 1 1 420px;
  min-width: 0;
}

.advertiser-main__title {
  font-size: 20px;
  font-weight: 700;
  color: #1f2a44;
}

.advertiser-main__desc {
  margin-top: 8px;
  max-width: 720px;
  line-height: 1.7;
  color: #6e7b90;
}

.advertiser-main__tags {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  justify-content: flex-end;
  align-items: center;
  margin-left: auto;
}

.advertiser-main__view-switch {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin-right: 6px;
}

.advertiser-main__view-label {
  font-size: 13px;
  color: #6e7b90;
}

.advertiser-main__table {
  margin-top: 18px;
  min-width: 0;
}

.advertiser-main__table /deep/ .table-body {
  background: #ffffff;
  border-radius: 18px;
  padding: 16px;
  box-shadow: 0 10px 28px rgba(31, 42, 68, 0.08);
  border: 1px solid rgba(103, 139, 255, 0.08);
}

.advertiser-main__table /deep/ .advertiser-tools-container {
  display: flex;
  flex-direction: column;
  gap: 12px;
  background: #ffffff;
  padding: 14px 18px;
  border-radius: 12px;
  border: 1px solid #e8eef9;
  box-shadow: 0 4px 12px rgba(31, 42, 68, 0.03);
  margin-bottom: 14px;
  flex-shrink: 0;
}

.advertiser-main__table /deep/ .advertiser-tools-container .el-button--small {
  padding: 8px 12px;
  font-size: 12px;
}

.advertiser-main__table /deep/ .el-table {
  border-radius: 16px;
  overflow: hidden;
}

.advertiser-main__table /deep/ .el-table::before {
  display: none;
}

.advertiser-main__table /deep/ .el-table thead th {
  background: #f7faff;
}

.advertiser-main__table /deep/ .advertiser-tools__actions-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
  padding-bottom: 12px;
  border-bottom: 1px solid #eef3fb;
}

.advertiser-main__table /deep/ .advertiser-tools__actions-bar .left-actions,
.advertiser-main__table /deep/ .advertiser-tools__actions-bar .right-actions {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
}

.advertiser-main__table /deep/ .advertiser-tools__actions-bar .left-actions {
  flex: 1 1 420px;
  min-width: 0;
}

.advertiser-main__table /deep/ .advertiser-tools__actions-bar .right-actions {
  justify-content: flex-end;
  margin-left: auto;
}

.advertiser-main__table /deep/ .advertiser-tools__actions-bar .right-actions .line {
  width: 1px;
  height: 16px;
  background-color: #dce6f5;
  margin: 0 4px;
}

.advertiser-main__table /deep/ .advertiser-tools__filters-bar {
  display: flex;
  align-items: flex-end;
  flex-wrap: wrap;
  gap: 10px;
  min-width: 0;
}

.advertiser-main__table /deep/ .advertiser-tools__filters-bar .filters-grid {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: 10px;
  flex: 1 1 720px;
  min-width: 0;
}

.advertiser-main__table /deep/ .advertiser-tools__filters-bar .filters-grid .filter-item {
  display: inline-flex;
  flex: 1 1 140px;
  min-width: 132px;
  max-width: 180px;
}

.advertiser-main__table /deep/ .advertiser-tools__filters-bar .el-select,
.advertiser-main__table /deep/ .advertiser-tools__filters-bar .el-input,
.advertiser-main__table /deep/ .advertiser-tools__filters-bar .el-date-editor {
  width: 100%;
  margin: 0;
}

.advertiser-main__table /deep/ .advertiser-tools__filters-bar .el-input__inner {
  border-radius: 8px;
}

.advertiser-main__table /deep/ .advertiser-tools__filters-bar .filters-actions {
  display: flex;
  justify-content: flex-end;
  margin-left: auto;
  flex: 0 0 auto;
}

.advertiser-main__table /deep/ .el-table .cell {
  line-height: 1.55;
}

.advertiser-card-view {
  padding-top: 4px;
}

.advertiser-card-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(310px, 1fr));
  gap: 14px;
}

.advertiser-card {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 14px;
  min-height: 260px;
  padding: 16px;
  border-radius: 18px;
  background: #ffffff;
  border: 1px solid #e6eefc;
  border-left: 5px solid #4e82ff;
  box-shadow: 0 12px 28px rgba(31, 42, 68, 0.08);
  transition: transform .18s ease, box-shadow .18s ease, border-color .18s ease;
}

.advertiser-card:hover {
  transform: translateY(-2px);
  border-color: #b9d0ff;
  box-shadow: 0 16px 36px rgba(31, 42, 68, 0.12);
}

.advertiser-card.is-readonly {
  opacity: .72;
  border-left-color: #9aa8bd;
}

.advertiser-card__header,
.advertiser-card__alert-top,
.advertiser-card__footer {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
}

.advertiser-card__name {
  max-width: 210px;
  font-size: 16px;
  font-weight: 700;
  color: #1f2a44;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.advertiser-card__subline {
  margin-top: 6px;
  display: flex;
  flex-direction: column;
  gap: 3px;
  font-size: 12px;
  color: #75839b;
  word-break: break-all;
}

.advertiser-card__metrics {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 10px;
}

.advertiser-card__metric {
  padding: 11px 10px;
  border-radius: 14px;
  background: #f7faff;
  border: 1px solid #edf3ff;
}

.advertiser-card__metric span {
  display: block;
  font-size: 12px;
  color: #7b89a0;
}

.advertiser-card__metric strong {
  display: block;
  margin-top: 5px;
  font-size: 15px;
  color: #1f2a44;
  word-break: break-all;
}

.advertiser-card__alert-box {
  padding: 13px 14px;
  border-radius: 16px;
  background: #fff8ed;
  border: 1px solid #ffe0b0;
}

.advertiser-card__alert-box.is-enabled {
  background: #f0fbf6;
  border-color: #c7efd9;
}

.advertiser-card__alert-title {
  font-size: 14px;
  font-weight: 700;
  color: #263653;
}

.advertiser-card__alert-desc,
.advertiser-card__alert-contacts {
  margin-top: 8px;
  line-height: 1.6;
  font-size: 12px;
  color: #6d7b92;
}

.advertiser-card__performance {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0;
  padding: 4px 0;
  border-top: 1px solid #eef2fb;
  border-bottom: 1px solid #eef2fb;
}

.advertiser-card__performance-item {
  padding: 10px 12px;
}

.advertiser-card__performance-item + .advertiser-card__performance-item {
  border-left: 1px solid #eef2fb;
}

.advertiser-card__performance-item span {
  display: block;
  font-size: 12px;
  color: #7b89a0;
}

.advertiser-card__performance-item strong {
  display: block;
  margin-top: 6px;
  font-size: 16px;
  font-weight: 700;
  color: #1f2a44;
  word-break: break-all;
}

.advertiser-card__performance-item--accent strong {
  color: #ff6a3d;
}

.advertiser-card__footer {
  margin-top: auto;
  align-items: center;
  justify-content: flex-end;
  flex-wrap: wrap;
  padding-top: 4px;
}

.advertiser-card-empty {
  min-height: 260px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  border-radius: 18px;
  background: #f8fbff;
  border: 1px dashed #cddcf4;
  color: #748198;
}

.advertiser-card-empty i {
  font-size: 34px;
  color: #9dafc9;
}

.advertiser-card-empty__title {
  margin-top: 12px;
  font-size: 15px;
  font-weight: 700;
  color: #2c3b57;
}

.advertiser-card-empty__desc {
  margin-top: 6px;
  font-size: 12px;
}

.group-dialog__title-wrap {
  padding-right: 30px;
}

.group-dialog__title {
  font-size: 20px;
  font-weight: 700;
  color: #1f2a44;
}

.group-dialog__subtitle {
  margin-top: 6px;
  line-height: 1.5;
  font-size: 13px;
  color: #748198;
}

.group-dialog {
  display: flex;
  gap: 18px;
}

.group-dialog__list-panel,
.group-dialog__editor-panel {
  border-radius: 20px;
  background: #f9fbff;
  border: 1px solid #e7eefc;
  padding: 20px;
  box-sizing: border-box;
}

.group-dialog__list-panel {
  width: 46%;
}

.group-dialog__editor-panel {
  flex: 1;
  background: linear-gradient(180deg, #ffffff 0%, #f8fbff 100%);
}

.group-dialog__panel-header,
.group-dialog__editor-hero {
  display: flex;
  justify-content: space-between;
  gap: 16px;
  align-items: flex-start;
}

.group-dialog__panel-title {
  font-size: 18px;
  font-weight: 700;
  color: #1f2a44;
}

.group-dialog__panel-desc {
  margin-top: 6px;
  line-height: 1.6;
  font-size: 13px;
  color: #748198;
}

.group-dialog__search {
  margin-top: 16px;
}

.group-dialog__list {
  margin-top: 16px;
  max-height: 460px;
  overflow-y: auto;
  padding-right: 4px;
}

.group-dialog__card {
  padding: 16px;
  border-radius: 18px;
  background: #ffffff;
  border: 1px solid #e7eefc;
  cursor: pointer;
  transition: all .2s ease;
}

.group-dialog__card + .group-dialog__card {
  margin-top: 12px;
}

.group-dialog__card:hover {
  border-color: #cfe0ff;
  box-shadow: 0 10px 24px rgba(86, 130, 255, 0.08);
}

.group-dialog__card.is-active {
  border-color: #7fa9ff;
  background: linear-gradient(135deg, #eef5ff 0%, #ffffff 100%);
  box-shadow: 0 12px 26px rgba(86, 130, 255, 0.12);
}

.group-dialog__card-top,
.group-dialog__card-footer {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  align-items: flex-start;
}

.group-dialog__card-title {
  font-size: 15px;
  font-weight: 700;
  color: #243250;
}

.group-dialog__card-meta {
  margin-top: 6px;
  font-size: 12px;
  color: #75839b;
}

.group-dialog__card-footer {
  margin-top: 12px;
  align-items: center;
  font-size: 12px;
  color: #8893a6;
}

.group-dialog__card-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.group-dialog__danger {
  color: #f56c6c;
}

.group-dialog__form {
  margin-top: 20px;
}

.group-dialog__tips {
  margin-top: 20px;
  padding: 18px;
  border-radius: 18px;
  background: #f5f9ff;
  border: 1px solid #e4ecfb;
}

.group-dialog__tips-title {
  font-size: 15px;
  font-weight: 700;
  color: #23314d;
}

.group-dialog__tip-item {
  position: relative;
  margin-top: 12px;
  padding-left: 14px;
  line-height: 1.6;
  font-size: 13px;
  color: #6d7b92;
}

.group-dialog__tip-item::before {
  content: '';
  position: absolute;
  top: 8px;
  left: 0;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #4e82ff;
}

.group-dialog__form-actions {
  margin-top: 22px;
  display: flex;
  justify-content: flex-end;
  gap: 12px;
}

.group-dialog__empty {
  padding: 42px 18px;
  border-radius: 18px;
  border: 1px dashed #d6e1f5;
  background: #ffffff;
  text-align: center;
  color: #7b889d;
}

.group-dialog__empty i {
  font-size: 28px;
  color: #95a7c2;
}

.group-dialog__empty-title {
  margin-top: 12px;
  font-size: 15px;
  font-weight: 600;
  color: #2c3b57;
}

.group-dialog__empty-desc {
  margin-top: 6px;
  line-height: 1.6;
  font-size: 12px;
}

.batch-group-dialog__summary {
  margin-bottom: 18px;
  padding: 14px 16px;
  border-radius: 14px;
  background: #f5f9ff;
  border: 1px solid #e4ecfb;
  line-height: 1.7;
  color: #5d6c84;
}

.batch-group-dialog__summary span {
  color: #316cff;
  font-size: 18px;
  font-weight: 700;
}

.batch-group-dialog__tips {
  margin-top: 8px;
}

.batch-group-dialog__tip {
  position: relative;
  padding-left: 12px;
  line-height: 1.7;
  font-size: 12px;
  color: #7a879d;
}

.batch-group-dialog__tip + .batch-group-dialog__tip {
  margin-top: 6px;
}

.batch-group-dialog__tip::before {
  content: '';
  position: absolute;
  top: 8px;
  left: 0;
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background: #4e82ff;
}

.image-popup {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(15, 23, 42, 0.82);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
  backdrop-filter: blur(4px);
}

.image-popup img {
  max-width: 90%;
  max-height: 90%;
  border-radius: 16px;
  box-shadow: 0 18px 42px rgba(15, 23, 42, 0.35);
}


.mask {
  background-color: rgba(15, 23, 42, 0.45);
  height: 100vh;
  width: 100vw;
  position: fixed;
  top: 0;
  left: 0;
  z-index: 100;

}

.popup_box {
  font-size: 14px;
  z-index: 101;
  width: min(860px, calc(100vw - 48px));
  max-height: calc(100vh - 56px);
  overflow-y: auto;
  padding: 18px 20px 22px;
  border-radius: 14px;
  background: linear-gradient(180deg, #fbfdff 0%, #ffffff 100%);
  border: 1px solid rgba(135, 163, 222, 0.2);
  box-shadow: 0 12px 32px rgba(15, 23, 42, 0.14);
}

.authorizeBox {
  cursor: pointer;
  border: 1px solid #dce6f5;
  border-radius: 12px;
  width: 350px;
  padding: 14px 16px 12px;
  min-height: 140px;
  box-sizing: border-box;
  transition: all .2s;
  display: inline-block;
  background: linear-gradient(180deg, #ffffff 0%, #f8fbff 100%);
  box-shadow: 0 10px 24px rgba(31, 42, 68, 0.06);
  color: #42536b;
}

.authorizeBox:hover {
  border-color: #5f8eff !important;
  color: #2d63d3 !important;
  box-shadow: 0 14px 28px rgba(76, 121, 217, 0.12);
  transform: translateY(-1px);
}

.activeAuthorizeBox {
  background: linear-gradient(180deg, #f7faff 0%, #eef4ff 100%);
  border-color: #5f8eff;
  color: #2d63d3 !important;
  box-shadow: 0 16px 30px rgba(76, 121, 217, 0.14);
}

.instanceDiagram {
  width: 700px;
  border-radius: 18px;
  box-shadow: 0 16px 32px rgba(15, 23, 42, 0.14);
}


@media (max-width: 1024px) {
  .advertiser-layout,
  .group-dialog {
    flex-direction: column;
  }

  .group-sidebar,
  .group-dialog__list-panel,
  .group-dialog__editor-panel {
    width: 100%;
  }

  .group-sidebar__list {
    max-height: 260px;
    overflow-y: auto;
  }

  .group-dialog__list {
    max-height: 300px;
    overflow-y: auto;
  }
}

@media (max-width: 768px) {
  .popup_box {
    width: calc(100vw - 24px);
    max-height: 80vh;
    overflow-y: auto;
    padding: 18px 16px 22px;
  }

  .instanceDiagram {
    width: 300px;
    box-shadow: 0 0 8px #00000029;
  }

  .authorizeBox {
    width: 160px;
    min-height: 200px;
  }

  .advertiser-page {
    padding: 12px;
  }

  .advertiser-main__hero {
    padding: 18px;
  }

  .advertiser-main__tags {
    justify-content: flex-start;
  }

  .advertiser-main__table /deep/ .table-body {
    padding: 16px;
  }

  /deep/ .el-dialog {
    width: 90% !important;
  }
}

/* --- 桌面大屏视口高度自适应锁定与局部滚动优化 --- */
@media (min-width: 1025px) {
  /* 限制整个页面为屏幕可用高度，禁止全局滚动 */
  .advertiser-page {
    height: calc(100vh - 120px) !important;
    display: flex;
    flex-direction: column;
    overflow: hidden;
  }

  /* 布局容器填满高度且不溢出 */
  .advertiser-layout {
    display: flex;
    gap: 14px;
    align-items: stretch;
    flex: 1;
    height: 0;
    overflow: hidden;
  }

  /* 左侧分组侧边栏锁定高度，列表独立滚动 */
  .group-sidebar {
    height: 100%;
    display: flex;
    flex-direction: column;
    overflow: hidden;
  }

  .group-sidebar__list {
    max-height: none !important; /* 覆盖原有固定的 calc 高度 */
    flex: 1;
    overflow-y: auto;
  }

  /* 右侧主面板高度锁定 */
  .advertiser-main {
    height: 100%;
    display: flex;
    flex-direction: column;
    overflow: hidden;
  }

  /* 表格包装区充满剩余空间 */
  .advertiser-main__table {
    margin-top: 18px;
    flex: 1;
    height: 0;
    display: flex;
    flex-direction: column;
    overflow: hidden;
  }

  /* 穿透 CommonTable 组件 */
  .advertiser-main__table /deep/ .table-container {
    height: 100%;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    min-width: 0;
  }

  .advertiser-main__table /deep/ .table-body {
    height: 100%;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    box-sizing: border-box;
    min-width: 0;
  }

  .advertiser-main__table /deep/ .advertiser-tools-container,
  .advertiser-main__table /deep/ .table-tools {
    flex-shrink: 0; /* 工具栏尺寸固定，不被压缩 */
  }

  /* 强制覆盖 CommonTable 内部 el-table 的行内 68vh 高度，使用 flex 自适应 */
  .advertiser-main__table /deep/ .el-table {
    flex: 1;
    height: 0 !important;
    width: 100% !important;
    min-width: 0;
  }

  /* 卡片模式下的插槽容器独立垂直滚动 */
  .advertiser-main__table /deep/ .common-table-slot-content {
    flex: 1;
    height: 0;
    overflow-y: auto;
  }
}
</style>
